import { CheckExportBtn } from '@/components/CheckExportBtn';
import { VatTreatmentFields, vatPayload, vatError } from '@/components/VatTreatmentFields';
import { DrawerSourceSelect } from '@/components/DrawerSourceSelect';
import { PurchaseVatReviewDialog } from '@/components/vatReview/PurchaseVatReviewDialog';
import { askCancel } from '@/lib/cancelReason';
import { handleExport, REPORT_EXPORT_COLS } from '@/lib/exportCsv';
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ReportHeader } from '@/components/reports/ReportUI';
import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, AlertTriangle, DollarSign, Settings2, MessageCircle, FileDown, RotateCcw, CalendarDays, ChevronDown, ChevronUp, ChevronRight, HelpCircle, Copy, Store, TrendingDown, ChevronLeft, Receipt, Landmark, Banknote, Car, Wrench, Recycle, CreditCard, Tag, Plane, UserCircle, MoreHorizontal, Package, Link as LinkIcon, BarChart2, Handshake, Cog, GraduationCap, ShieldCheck, Gift, HeartHandshake, Printer, Fuel, History, Truck, Megaphone, Pin, Search, X, CalendarCheck2, Sparkles, CheckCircle2, Pencil, ScanLine, Download, SlidersHorizontal } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import BranchLink from '@/components/BranchLink';
import { RiyalIcon, cur } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { format, startOfMonth, endOfMonth, parseISO } from 'date-fns';

// iter544 — Safe wrappers around date-fns `format()`. The Expenses page
// crashed in production with `Invalid time value` because ONE row in the
// DB had a malformed/empty `date` string, and the whole tree errored.
// These helpers swallow bad inputs and return an em-dash so the page
// still renders.
const safeFormat = (value, pattern, fallback = '—') => {
  if (!value) return fallback;
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return fallback;
    return format(d, pattern);
  } catch {
    return fallback;
  }
};
const safeNewDate = (value) => {
  if (!value) return new Date(NaN);
  const d = value instanceof Date ? value : new Date(value);
  return d;
};
import { ExportButtons } from '@/components/ExportButtons';
import { VatModeToggle } from '@/components/common/VatModeToggle';
import { useVatModeStore } from '@/stores/vatModeStore';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { InlineEditCell } from '@/components/common/InlineEditCell';
import { useTaxConfig } from '@/hooks/useTaxConfig';
import BranchTaxBadge from '@/components/BranchTaxBadge';
import { WhatsAppSendDialog } from '@/components/WhatsAppSendDialog';
import PickFromBankDialog from '@/components/banking/PickFromBankDialog';
import { AdvancedSearch, applySearchFilters } from '@/components/AdvancedSearch';
import { useBranchStore, useAuthStore } from '@/stores';
import { FinancialSummaryHeader } from '@/components/FinancialSummaryHeader';
import { VirtualizedTable } from '@/components/VirtualizedTable';
import { PDFExportButton } from '@/components/PDFExportButton';
import { DateQuickFilter } from '@/components/DateQuickFilter';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { FilterEmptyNudge } from '@/components/common/FilterEmptyNudge';
import { DuplicatePeekList } from '@/components/common/DuplicatePeekList';
import { SearchableSelect } from '@/components/SearchableSelect';
import { localToday, localDateTimeStamp, localMonthStart } from '@/lib/dateRange';
import { DuplicateExpensesScanner } from '@/components/DuplicateExpensesScanner';
import { ExpensesFlatTable } from '@/components/expenses/ExpensesFlatTable';
import { useRealtimeRefresh } from '@/hooks/useRealtimeRefresh';
import { useCategorySearch } from '@/hooks/useCategorySearch';
import { ExpenseSummaryCards } from '@/components/expenses/ExpenseSummaryCards';
import { PeriodAccountingPanel } from '@/components/expenses/PeriodAccountingPanel';

// AI category suggestion disabled by policy (deterministic mappings only); code kept.
const AI_CATEGORY_SUGGEST = false;

// iter541 — Tiny inline lists rendering "Already booked" rows with an
// Undo button each. They live outside ExpensesPage so they're stable
// across renders.
function FeeUndoList({ months, onUndo, onEdit, onNavigate, fmtMonthLabel }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="fee-undo-list">
      {months.map(m => (
        <div
          key={m.ym}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-emerald-300 bg-emerald-50 text-[11px] hover:bg-emerald-100 transition-colors"
          data-testid={`fee-booked-chip-${m.ym}`}
        >
          <CheckCircle2 size={11} className="text-emerald-600" />
          <button
            type="button"
            onClick={() => onNavigate?.(m.expense_id)}
            disabled={!m.expense_id}
            className="font-medium text-emerald-900 hover:underline underline-offset-2 disabled:no-underline"
            title="Open the linked expense"
            data-testid={`fee-navigate-${m.ym}`}
          >
            {fmtMonthLabel(m.ym)}
          </button>
          <span className="font-mono text-emerald-700">{cur()} {Number(m.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
          {m.expense_id && (
            <button
              type="button"
              onClick={() => onEdit?.(m.expense_id)}
              className="ml-0.5 text-emerald-700 hover:text-emerald-900 shrink-0"
              data-testid={`fee-edit-${m.ym}`}
              title="Edit this expense (date / amount / payment mode)"
            >
              <Pencil size={11} />
            </button>
          )}
          {m.marker_id && (
            <button
              type="button"
              onClick={() => onUndo(m.marker_id)}
              className="ml-0.5 text-[10px] text-rose-700 hover:text-rose-900 underline-offset-2 hover:underline shrink-0"
              data-testid={`fee-undo-${m.ym}`}
            >
              Undo
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function EmpUndoList({ rows, onUndo, onEdit, onNavigate }) {
  return (
    <div className="space-y-1" data-testid="emp-undo-list">
      {rows.map(t => (
        <div
          key={t.txn_id}
          className="flex items-center gap-2 px-2 py-1 rounded-md border border-emerald-300 bg-emerald-50 text-[11px] hover:bg-emerald-100 transition-colors"
          data-testid={`emp-booked-row-${t.txn_id}`}
        >
          <CheckCircle2 size={11} className="text-emerald-600 shrink-0" />
          <span className="font-mono text-emerald-700 shrink-0">{(t.date || '').slice(0, 10)}</span>
          <button
            type="button"
            onClick={() => onNavigate?.(t.expense_id)}
            disabled={!t.expense_id}
            className="flex-1 min-w-0 truncate text-left text-emerald-900 hover:underline underline-offset-2 disabled:no-underline"
            title="Open the linked expense in the table below"
            data-testid={`emp-navigate-${t.txn_id}`}
          >
            {t.description}
          </button>
          <span className="font-mono font-bold text-emerald-900 shrink-0">{cur()} {Number(t.debit || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
          {t.expense_id && (
            <button
              type="button"
              onClick={() => onEdit?.(t.expense_id)}
              className="text-emerald-700 hover:text-emerald-900 shrink-0"
              data-testid={`emp-edit-${t.txn_id}`}
              title="Edit this expense (date / amount / payment mode)"
            >
              <Pencil size={11} />
            </button>
          )}
          {t.marker_id && (
            <button
              type="button"
              onClick={() => onUndo(t.marker_id)}
              className="text-[10px] text-rose-700 hover:text-rose-900 underline-offset-2 hover:underline shrink-0"
              data-testid={`emp-undo-${t.txn_id}`}
            >
              Undo
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ExpensesPage() {
  const { t } = useLanguage();
  const [expenses, setExpenses] = useState([]);
  const [lostPaymentsScan, setLostPaymentsScan] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const { branches, fetchBranches } = useBranchStore();
  const { user } = useAuthStore();
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [partners, setPartners] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [branchSummary, setBranchSummary] = useState(null);
  // iter944 — Hide/Show summary header for more table space (persisted)
  const [showHeader, setShowHeader] = useState(() => {
    try { return localStorage.getItem('expenses_show_header') !== '0'; } catch { return true; }
  });
  const toggleHeader = () => setShowHeader((v) => { const n = !v; try { localStorage.setItem('expenses_show_header', n ? '1' : '0'); } catch {} return n; });
  // H4.3 — optional bank-transfer hint banner (persisted via the same lightweight localStorage pattern as showHeader).
  const [showHint, setShowHint] = useState(() => {
    try { return localStorage.getItem('expenses_show_hint') !== '0'; } catch { return true; }
  });
  const toggleHint = () => setShowHint((v) => { const n = !v; try { localStorage.setItem('expenses_show_hint', n ? '1' : '0'); } catch {} return n; });
  const [loading, setLoading] = useState(true);
  const [searchFilters, setSearchFilters] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlDateFilter = searchParams.get('date');
  const urlStartDate = searchParams.get('start_date');
  const urlEndDate = searchParams.get('end_date');
  const urlBranchFilter = searchParams.get('branch');
  const urlFocus = searchParams.get('focus');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Daily-grouped view is optional; default is a flat transaction list so
  // 10/25/50 page sizes map 1:1 to transaction rows.
  const [dailyView, setDailyView] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [serverTotals, setServerTotals] = useState({ total_amount: 0, cash_total: 0, bank_total: 0, credit_total: 0 });
  const inclVat = useVatModeStore((s) => s.inclVat);
  const [expandedDates, setExpandedDates] = useState({});
  const [dateRange, setDateRange] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [duplicateExisting, setDuplicateExisting] = useState([]);
  const [liveDup, setLiveDup] = useState(null);   // iter818 — live pre-save duplicate guard
  const [activeBackendFilters, setActiveBackendFilters] = useState({});
  const [branchSel, setBranchSel] = useState([]);
  const [includePivot, setIncludePivot] = useState(false);

  useEffect(() => {
    // iter923 — deep-link seeding: prefer an explicit start_date/end_date
    // range (e.g. from the COGS "Open in Expenses" jump); fall back to a
    // single `date` param for backward compatibility.
    if (urlStartDate || urlEndDate) {
      const range = { start: urlStartDate || urlEndDate, end: urlEndDate || urlStartDate };
      setDateRange(range);
      setSearchFilters(prev => ({ ...prev, date: { ...range } }));
    } else if (urlDateFilter) {
      setDateRange({ start: urlDateFilter, end: urlDateFilter });
      setSearchFilters(prev => ({ ...prev, date: { start: urlDateFilter, end: urlDateFilter } }));
    }
    if (urlBranchFilter) {
      setActiveBackendFilters(prev => ({ ...prev, branch_id: urlBranchFilter }));
    }
  }, [urlDateFilter, urlStartDate, urlEndDate, urlBranchFilter]);

  // iter473 — Deep-link from Inter-Branch Ledger / other pages: open the
  // Add Expense dialog automatically when `?action=add` is in the URL.
  // `cross_branch=1` is reserved for future deep-link UX (e.g. inline
  // banner reminding the user to pick "expense for branch").
  useEffect(() => {
    if (searchParams.get('action') === 'add') {
      setShowAddDialog(true);
      if (searchParams.get('cross_branch') === '1') {
        setCrossBranchHint(true);
      }
      // strip the params so a refresh / back nav doesn't reopen the dialog
      const next = new URLSearchParams(searchParams);
      next.delete('action'); next.delete('cross_branch');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showCatManager, setShowCatManager] = useState(false);
  const [showVatReview, setShowVatReview] = useState(false);
  useEffect(() => {
    if (searchParams.get('vat_review') !== '1') return;
    setShowVatReview(true);
    const next = new URLSearchParams(searchParams); next.delete('vat_review');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { hasPermission } = useAuthStore();
  const [showAddDialog, setShowAddDialog] = useState(false);   // iter442 — gate the inline form behind a modal
  // iter474 — show a one-liner hint when the dialog was deep-linked with cross_branch=1
  const [crossBranchHint, setCrossBranchHint] = useState(false);
  const [showPickBank, setShowPickBank] = useState(false);     // iter442e — bank-statement picker
  const [bulkPickMode, setBulkPickMode] = useState(false);     // iter448 — single vs bulk pick mode
  const [pickedBankTxn, setPickedBankTxn] = useState(null);    // iter442e — the txn we'll link on submit
  const [showRenewDialog, setShowRenewDialog] = useState(false);
  const [renewingRec, setRenewingRec] = useState(null);
  const [renewData, setRenewData] = useState({ amount: '', payment_mode: 'cash', branch_id: '' });
  const [newCat, setNewCat] = useState('');
  const [newSubCat, setNewSubCat] = useState({ name: '', parent: '' });
  const [newRecData, setNewRecData] = useState({ name: '', category: 'rent', amount: '', frequency: 'monthly', branch_id: '', next_due_date: '', alert_days: 7 });
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundData, setRefundData] = useState({ amount: '', reason: '', refund_mode: 'cash', category: 'Refund', branch_id: '', date: localToday() });
  const [editExpense, setEditExpense] = useState(null);
  const [editData, setEditData] = useState({});
  const [bulkVatMode, setBulkVatMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkVatRate, setBulkVatRate] = useState(15);
  const [bulkVatInclusive, setBulkVatInclusive] = useState(true);
  const [bulkSkipExisting, setBulkSkipExisting] = useState(true);
  const [bulkFilterSupplier, setBulkFilterSupplier] = useState('');
  const [bulkFilterStartDate, setBulkFilterStartDate] = useState('');
  const [bulkFilterEndDate, setBulkFilterEndDate] = useState('');
  const [categoryTree, setCategoryTree] = useState([]);
  const [quickMapCat, setQuickMapCat] = useState(null); // {category, suggested_account_name}
  const [quickMapAccount, setQuickMapAccount] = useState('');
  const [quickMapScope, setQuickMapScope] = useState('all'); // 'all' | 'once'
  const [coaAccounts, setCoaAccounts] = useState([]);

  const openQuickMap = async (categoryName) => {
    setQuickMapCat({ category: categoryName });
    setQuickMapAccount('');
    setQuickMapScope('all');
    let accts = coaAccounts;
    if (accts.length === 0) {
      try {
        const r = await api.get('/accounting/accounts');
        accts = (r.data || []).filter(a => ['expense', 'liability', 'asset'].includes(a.type));
        setCoaAccounts(accts);
      } catch { /* non-fatal */ }
    }
    // Pre-select the account this category currently posts to (so it's an edit)
    try {
      const r = await api.get(`/accounting/category-account-resolve?category=${encodeURIComponent(categoryName)}`);
      const cur = r.data || {};
      const match = cur.account_id || (accts.find(a => a.name === cur.account_name)?.id);
      if (match) setQuickMapAccount(match);
    } catch { /* non-fatal */ }
  };

  const saveQuickMap = async () => {
    if (!quickMapAccount || !quickMapCat) return;
    const acct = coaAccounts.find(a => a.id === quickMapAccount);
    // "Only this expense" — stash an override on the form, change no mapping.
    if (quickMapScope === 'once') {
      setFormData(prev => ({ ...prev, account_override: acct?.name || '' }));
      toast.success(`This expense will post to "${acct?.name}" (mapping unchanged)`);
      setQuickMapCat(null);
      setQuickMapAccount('');
      return;
    }
    try {
      await api.post('/accounting/category-account-mappings', {
        category: quickMapCat.category,
        account_id: quickMapAccount,
      });
      // a per-expense override no longer applies once the category is remapped
      setFormData(prev => ({ ...prev, account_override: undefined }));
      toast.success(`"${quickMapCat.category}" → ${acct?.name} (all future expenses)`);
      setQuickMapCat(null);
      setQuickMapAccount('');
      // refetch the tree so the preview flips from warning to green
      try {
        const r = await api.get('/accounting/expense-category-tree');
        setCategoryTree(r.data?.tree || []);
      } catch { /* UI will update on next fetch */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save mapping');
    }
  };

  const [formData, setFormData] = useState({
    category: '', sub_category: '', description: '', amount: '',
    payment_mode: 'cash', branch_id: '', expense_for_branch_id: '', supplier_id: '', employee_id: '',
    date: localToday(), notes: '', bill_image_url: '', invoice_number: '',
    vat_inclusive: true, vat_rate: 15, vat_enabled: true,
    is_advance: false, advance_person_type: 'employee', advance_person_id: '',
    // iter526 — inline Fine attribution (only used when category contains 'fine')
    fine_vehicle_id: '', fine_plate_number: '', fine_violation_date: '',
    fine_department: '', fine_deduct_from_salary: false, fine_monthly_deduction: '',
  });

  // Clear a per-expense account override whenever the category changes.
  useEffect(() => {
    setFormData(prev => (prev.account_override ? { ...prev, account_override: undefined } : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.category]);


  // iter526 — auto-detect when the chosen category is a fine
  const _catCombined = `${formData.category || ''} ${formData.sub_category || ''}`.toLowerCase();
  const isFineCategory = /\bfine|fines|saher|moi|traffic|penalt/.test(_catCombined);

  // iter540 — When the user picks a bank-fee / VAT-on-fee / POS-fee /
  // SADAD category, we surface the unbooked amounts already detected on
  // their bank statements so they can one-click book all months at once
  // instead of typing them in manually.
  const FEE_CAT_MAP = {
    'bank fees':            'bank_fees',
    'vat on bank fees':     'vat_fees',
    'vat on bank charges':  'vat_fees',
    'pos fees':             'pos_fees',
    'government fees':      'sadad_payment',
    'sadad':                'sadad_payment',
  };
  const feeCatCode = FEE_CAT_MAP[(formData.category || '').toLowerCase().trim()] || null;
  const [feeBookingData, setFeeBookingData] = useState(null);  // {category, label, months, total_pending_amount, total_pending_months}
  const [feeBookingLoading, setFeeBookingLoading] = useState(false);
  const [feeBookingPosting, setFeeBookingPosting] = useState(false);
  const [feeBookingPicked, setFeeBookingPicked] = useState({});  // {ym: bool}

  useEffect(() => {
    if (!feeCatCode) { setFeeBookingData(null); setFeeBookingPicked({}); return; }
    let alive = true;
    setFeeBookingLoading(true);
    api.get(`/bank-statements/pending-fee-bookings?category=${feeCatCode}`)
      .then(r => {
        if (!alive) return;
        const cat = r.data?.categories?.[feeCatCode] || null;
        setFeeBookingData(cat);
        // Pre-select all pending months by default
        const picked = {};
        for (const m of (cat?.months || [])) { if (!m.booked && m.bookable !== false) picked[m.ym] = true; }
        setFeeBookingPicked(picked);
      })
      .catch(() => { if (alive) setFeeBookingData(null); })
      .finally(() => { if (alive) setFeeBookingLoading(false); });
    return () => { alive = false; };
  }, [feeCatCode]);

  const fmtMonthLabel = (ym) => {
    if (!ym || ym.length < 7) return ym;
    const yy = ym.slice(0, 4); const mm = ym.slice(5, 7);
    return new Date(parseInt(yy), parseInt(mm) - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
  };

  const bookSelectedFeeMonths = async () => {
    if (!feeCatCode || !feeBookingData) return;
    const months = Object.entries(feeBookingPicked).filter(([, v]) => v).map(([k]) => k);
    if (months.length === 0) { toast.error('Pick at least one month'); return; }
    setFeeBookingPosting(true);
    try {
      const r = await api.post('/bank-statements/book-fee-bulk', {
        category: feeCatCode,
        branch_id: formData.branch_id || null,
        year_months: months,
      });
      const created = r.data?.created || [];
      const skipped = r.data?.skipped || [];
      if (created.length > 0) {
        const total = created.reduce((a, c) => a + (c.amount || 0), 0);
        toast.success(`Booked ${created.length} month${created.length === 1 ? '' : 's'} · ${cur()} ${total.toLocaleString(undefined, { maximumFractionDigits: 0 })} — auto-marked in your bank statements`);
      }
      if (skipped.length > 0) {
        toast.info(`${skipped.length} month${skipped.length === 1 ? '' : 's'} already booked — skipped`);
      }
      // Reset and refresh
      setShowAddDialog(false);
      setFormData({ category: '', sub_category: '', description: '', amount: '', payment_mode: 'cash', branch_id: '', expense_for_branch_id: '', supplier_id: '', employee_id: '', date: localToday(), notes: '', invoice_number: '', vat_inclusive: true, vat_rate: 15, vat_enabled: true, fine_vehicle_id: '', fine_plate_number: '', fine_violation_date: '', fine_department: '', fine_deduct_from_salary: false, fine_monthly_deduction: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed');
    } finally {
      setFeeBookingPosting(false);
    }
  };

  // iter541 — Employee-linked categories (Salary / Iqama Renewal /
  // Tickets / ID Card). Unlike fee categories, each bank txn books as
  // its own expense with an employee_id, so the UI shows a table of
  // bank txns + per-row employee dropdown + checkboxes.
  const EMP_CAT_MAP = {
    'salary':         'salary',
    'tickets':        'tickets',
    'id card':        'id_card',
    'iqama renewal':  'iqama_renewal',
    'government fees': null,  // ambiguous — only Iqama Renewal sub maps
  };
  // Allow both main category and "Government Fees > Iqama Renewal" sub
  const _mainLc = (formData.category || '').toLowerCase().trim();
  const _subLc = (formData.sub_category || '').toLowerCase().trim();
  const empCatCode = EMP_CAT_MAP[_mainLc]
    || (_mainLc === 'government fees' && _subLc === 'iqama renewal' ? 'iqama_renewal' : null);
  const [empBookingData, setEmpBookingData] = useState(null);
  const [empBookingLoading, setEmpBookingLoading] = useState(false);
  const [empBookingPosting, setEmpBookingPosting] = useState(false);
  // {txn_id: {picked: bool, employee_id: str|null}}
  const [empRowState, setEmpRowState] = useState({});

  useEffect(() => {
    if (!empCatCode) { setEmpBookingData(null); setEmpRowState({}); return; }
    let alive = true;
    setEmpBookingLoading(true);
    api.get(`/bank-statements/pending-employee-fee-txns?category=${empCatCode}`)
      .then(r => {
        if (!alive) return;
        const data = r.data || null;
        setEmpBookingData(data);
        // Default: all pending rows picked + suggested_employee_id pre-filled
        const next = {};
        for (const t of (data?.txns || [])) {
          if (!t.booked) {
            next[t.txn_id] = {
              picked: true,
              employee_id: t.suggested_employee_id || '',
            };
          }
        }
        setEmpRowState(next);
      })
      .catch(() => { if (alive) setEmpBookingData(null); })
      .finally(() => { if (alive) setEmpBookingLoading(false); });
    return () => { alive = false; };
  }, [empCatCode]);

  const bookSelectedEmpTxns = async () => {
    if (!empCatCode || !empBookingData) return;
    const assignments = [];
    for (const t of (empBookingData.txns || [])) {
      const rs = empRowState[t.txn_id];
      if (!rs || !rs.picked || t.booked) continue;
      assignments.push({
        statement_id: t.statement_id,
        txn_index: t.txn_index,
        employee_id: rs.employee_id || null,
        branch_id: formData.branch_id || t.branch_id || null,
      });
    }
    if (assignments.length === 0) { toast.error('Pick at least one row'); return; }
    setEmpBookingPosting(true);
    try {
      const r = await api.post('/bank-statements/book-employee-fee-bulk', {
        category: empCatCode,
        assignments,
      });
      const created = r.data?.created || [];
      const skipped = r.data?.skipped || [];
      if (created.length > 0) {
        const total = created.reduce((a, c) => a + (c.amount || 0), 0);
        toast.success(`Booked ${created.length} entr${created.length === 1 ? 'y' : 'ies'} · ${cur()} ${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
      }
      if (skipped.length > 0) toast.info(`${skipped.length} skipped (already booked or invalid)`);
      setShowAddDialog(false);
      setFormData({ category: '', sub_category: '', description: '', amount: '', payment_mode: 'cash', branch_id: '', expense_for_branch_id: '', supplier_id: '', employee_id: '', date: localToday(), notes: '', invoice_number: '', vat_inclusive: true, vat_rate: 15, vat_enabled: true, fine_vehicle_id: '', fine_plate_number: '', fine_violation_date: '', fine_department: '', fine_deduct_from_salary: false, fine_monthly_deduction: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed');
    } finally {
      setEmpBookingPosting(false);
    }
  };

  // iter547 — Inline edit for already-booked auto-entries. Pencil
  // button on each row opens this lightweight dialog so the user can
  // quickly fix the date / amount / payment mode without leaving the
  // banner. Date format is YYYY-MM-DD (ISO). Saving PUTs to /api/expenses/{id}.
  const [editingExpense, setEditingExpense] = useState(null);  // {id, date, amount, payment_mode, notes}
  const openAutoBookedEdit = async (expenseId) => {
    if (!expenseId) return;
    try {
      const r = await api.get(`/expenses/${expenseId}`);
      const e = r.data || {};
      setEditingExpense({
        id: e.id,
        date: (e.date || '').slice(0, 10) || localToday(),
        amount: String(e.amount ?? ''),
        payment_mode: e.payment_mode || 'bank',
        notes: e.notes || '',
        description: e.description || '',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load expense');
    }
  };
  const saveExpenseEdit = async () => {
    if (!editingExpense?.id) return;
    const amt = Number.parseFloat(editingExpense.amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error('Amount must be a positive number'); return; }
    if (!editingExpense.date) { toast.error('Pick a date'); return; }
    try {
      await api.put(`/expenses/${editingExpense.id}`, {
        date: editingExpense.date,
        amount: amt,
        payment_mode: editingExpense.payment_mode,
        notes: editingExpense.notes || null,
      });
      toast.success('Expense updated');
      setEditingExpense(null);
      // Refresh both the banners and the main list
      fetchData();
      if (feeCatCode) {
        try {
          const r = await api.get(`/bank-statements/pending-fee-bookings?category=${feeCatCode}`);
          setFeeBookingData(r.data?.categories?.[feeCatCode] || null);
        } catch { /* noop */ }
      }
      if (empCatCode) {
        try {
          const r = await api.get(`/bank-statements/pending-employee-fee-txns?category=${empCatCode}`);
          setEmpBookingData(r.data || null);
        } catch { /* noop */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  // iter547 — Click an already-booked row to jump to that expense in
  // the main Expenses table below. We highlight the row briefly so the
  // user can spot it. (Doesn't close the Add dialog.)
  const navigateToExpense = (expenseId) => {
    if (!expenseId) return;
    setShowAddDialog(false);
    // Defer to next tick so the dialog closes first
    setTimeout(() => {
      const el = document.querySelector(`[data-expense-id="${expenseId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-emerald-400', 'bg-emerald-50');
        setTimeout(() => el.classList.remove('ring-2', 'ring-emerald-400', 'bg-emerald-50'), 2400);
      } else {
        toast.info('Expense not visible in current filter — clear filters and try again.');
      }
    }, 200);
  };

  // iter541 — Undo any auto-booked entry (inline button in the banner).
  // Deletes both the expense and the marker so the row/month becomes
  // "pending" again.
  const undoAutoBooked = async (markerId, kind /* 'fee' | 'employee' */) => {
    if (!window.confirm('Undo this booking?\nThe linked expense will be deleted and the bank line will become pending again.')) return;
    try {
      await api.delete(`/bank-statements/auto-booked-entries-list/${markerId}`);
      toast.success('Undone — pending again');
      // Refresh whichever banner is showing
      if (kind === 'fee' && feeCatCode) {
        const r = await api.get(`/bank-statements/pending-fee-bookings?category=${feeCatCode}`);
        setFeeBookingData(r.data?.categories?.[feeCatCode] || null);
      }
      if (kind === 'employee' && empCatCode) {
        const r = await api.get(`/bank-statements/pending-employee-fee-txns?category=${empCatCode}`);
        setEmpBookingData(r.data || null);
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
    }
  };


  // iter526 — fleet vehicles for fine attribution
  const [fleetVehicles, setFleetVehicles] = useState([]);
  useEffect(() => {
    if (!isFineCategory) return;
    if (fleetVehicles.length > 0) return;
    api.get('/fleet/vehicles').then(r => setFleetVehicles(r.data || [])).catch(() => {});
  }, [isFineCategory, fleetVehicles.length]);

  // Pull central tax config (respects per-branch overrides)
  const { taxConfig } = useTaxConfig(formData.expense_for_branch_id || formData.branch_id);
  useEffect(() => {
    if (!taxConfig) return;
    setFormData(fd => ({
      ...fd,
      vat_enabled: !!taxConfig.vat_enabled,
      vat_rate: taxConfig.vat_rate || 15,
      vat_inclusive: !!taxConfig.vat_inclusive,
    }));
  }, [taxConfig?.vat_enabled, taxConfig?.vat_rate, taxConfig?.vat_inclusive, taxConfig?.source]);

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  // Calculate branch-wise expenses from filtered data (respects date/branch filters)
  const branchMonthlyExpenses = useMemo(() => {
    // Use server-provided branch breakdown (covers ALL records, not just displayed page)
    if (serverTotals.branch_breakdown && Object.keys(serverTotals.branch_breakdown).length > 0) {
      const byBranch = serverTotals.branch_breakdown;
      const totalAll = serverTotals.total_amount || Object.values(byBranch).reduce((s, b) => s + (b.total || 0), 0);
      
      const effectiveStart = searchFilters?.date?.start || dateRange?.start;
      const effectiveEnd = searchFilters?.date?.end || dateRange?.end;
      let label = 'All Time';
      if (effectiveStart && effectiveEnd) {
        label = effectiveStart === effectiveEnd ? effectiveStart : `${effectiveStart} to ${effectiveEnd}`;
      } else if (effectiveStart) {
        label = `From ${effectiveStart}`;
      }
      return { byBranch, totalAll, month: label };
    }
    
    // Fallback: calculate from displayed expenses
    const byBranch = {};
    let totalAll = 0;
    expenses.forEach(exp => {
      const branchId = exp.branch_id;
      const branch = branches.find(b => b.id === branchId);
      const branchName = branch?.name || 'Unassigned';
      if (!byBranch[branchName]) byBranch[branchName] = { cash: 0, bank: 0, credit: 0, total: 0, branch_id: branch?.id || null };
      const amt = exp.amount || 0;
      if (exp.payment_mode === 'cash') byBranch[branchName].cash += amt;
      else if (exp.payment_mode === 'bank') byBranch[branchName].bank += amt;
      else if (exp.payment_mode === 'credit') byBranch[branchName].credit += amt;
      byBranch[branchName].total += amt;
      totalAll += amt;
    });
    return { byBranch, totalAll, month: format(new Date(), 'MMMM yyyy') };
  }, [expenses, branches, dateRange, searchFilters, serverTotals]);

  // Category translation helper
  const catMap = {
    'Salary': 'cat_salary', 'Rent': 'cat_rent', 'Utilities': 'cat_utilities', 'Vehicle': 'cat_vehicle',
    'Maintenance': 'cat_maintenance', 'Supplier': 'cat_supplier', 'Tickets': 'cat_tickets',
    'ID Card': 'cat_id_card', 'Other': 'cat_other',
    'Basic Salary': 'cat_basic_salary', 'Overtime': 'cat_overtime', 'Bonus': 'cat_bonus',
    'Office Rent': 'cat_office_rent', 'Warehouse Rent': 'cat_warehouse_rent', 'Shop Rent': 'cat_shop_rent',
    'Electricity': 'cat_electricity', 'Water': 'cat_water', 'Internet': 'cat_internet', 'Phone': 'cat_phone',
    'Fuel': 'cat_fuel', 'Insurance': 'cat_insurance', 'Office Maintenance': 'cat_office_maintenance',
    'Equipment Repair': 'cat_equipment_repair'
  };
  const tc = (cat) => t(catMap[cat]) || cat;

  const defaultCats = [
    { name: 'Salary', subs: ['Basic Salary', 'Overtime', 'Bonus'], icon: DollarSign, color: 'bg-orange-100 border-orange-300 text-orange-700', group: 'operations' },
    { name: 'Rent', subs: ['Office Rent', 'Warehouse Rent', 'Shop Rent'], icon: Store, color: 'bg-success/20 border-success/30 text-success', group: 'operations' },
    { name: 'Utilities', subs: ['Electricity', 'Water', 'Internet', 'Phone'], icon: Wrench, color: 'bg-info/20 border-info/30 text-info', group: 'operations' },
    { name: 'Government', subs: ['ID Card Renewal', 'Visa Fee', 'Municipality License', 'GOSI', 'Qiwa', 'Muqeem', 'Baladiya Health Card', 'CR Renewal', 'SAGIA License Renewal'], icon: Landmark, color: 'bg-purple-100 border-purple-300 text-purple-700', group: 'compliance' },
    { name: 'Subscription Fees', subs: ['Qiwa Fee', 'Mudat Fee', 'Other Subscriptions'], icon: Tag, color: 'bg-cyan-100 border-cyan-300 text-cyan-700', group: 'compliance' },
    { name: 'Vehicle', subs: ['Fuel', 'Maintenance', 'Insurance', 'Parking', 'Car Wash', 'Toll'], icon: Car, color: 'bg-warning/20 border-warning/30 text-warning', group: 'operations' },
    { name: 'Maintenance', subs: ['Office Maintenance', 'Equipment Repair', 'AC Maintenance'], icon: Wrench, color: 'bg-muted border-border text-foreground', group: 'operations' },
    { name: 'Assets', subs: ['AC Unit', 'Fridge / Freezer', 'Computer / Laptop', 'Furniture', 'Kitchen Equipment', 'POS Machine', 'CCTV / Camera', 'Printer', 'Other Equipment'], icon: Package, color: 'bg-violet-100 border-violet-300 text-violet-700', group: 'operations' },
    { name: 'Office Equipment', subs: ['Small Equipment', 'Tools'], icon: Cog, color: 'bg-zinc-100 border-zinc-300 text-zinc-700', group: 'operations' },
    { name: 'Cleaning', subs: ['Cleaning Supplies', 'Hygiene', 'Pest Control'], icon: Recycle, color: 'bg-teal-100 border-teal-300 text-teal-700', group: 'operations' },
    { name: 'Security', subs: ['Guard Services', 'CCTV', 'Alarm Monitoring'], icon: ShieldCheck, color: 'bg-slate-100 border-slate-300 text-slate-700', group: 'operations' },
    { name: 'Waste Management', subs: [], icon: Recycle, color: 'bg-success/20 border-success/30 text-success', group: 'operations' },
    { name: 'Bank Fees', subs: ['Bank Charges', 'Transfer Fees', 'POS Machine Fees', 'Card Fees', 'Merchant Fees'], icon: Banknote, color: 'bg-indigo-100 border-indigo-300 text-indigo-700', group: 'financial' },
    { name: 'Supplier', subs: [], icon: CreditCard, color: 'bg-error/20 border-error/30 text-error', group: 'financial' },
    // iter442d — Agent / commission-style payouts (real estate, freight, finder fees, etc.)
    { name: 'Agent Fee', subs: ['Broker Fee', 'Referral Fee', 'Commission'], icon: Handshake, color: 'bg-amber-100 border-amber-300 text-amber-700', group: 'financial' },
    { name: 'Loan Repayment', subs: ['Bank Loan', 'Personal Loan', 'Asset Loan'], icon: TrendingDown, color: 'bg-rose-100 border-rose-300 text-rose-700', group: 'financial' },
    // iter442d — Software & subscriptions (SaaS, hosting, domains)
    { name: 'Software', subs: ['SaaS Subscription', 'Cloud', 'Hosting', 'Domain'], icon: Cog, color: 'bg-blue-100 border-blue-300 text-blue-700', group: 'financial' },
    { name: 'Tickets', subs: [], icon: Plane, color: 'bg-info/20 border-info/30 text-info', group: 'employee' },
    { name: 'ID Card', subs: [], icon: UserCircle, color: 'bg-pink-100 border-pink-300 text-pink-700', group: 'employee' },
    { name: 'Travel', subs: ['Air Tickets', 'Accommodation / Hotel', 'Per Diem'], icon: Plane, color: 'bg-sky-100 border-sky-300 text-sky-700', group: 'employee' },
    // iter442d — Training & development
    { name: 'Training', subs: ['Courses', 'Certification', 'Workshops'], icon: GraduationCap, color: 'bg-lime-100 border-lime-300 text-lime-700', group: 'employee' },
    { name: 'Insurance', subs: ['Vehicle', 'Health / Medical', 'Property', 'Liability'], icon: ShieldCheck, color: 'bg-emerald-100 border-emerald-300 text-emerald-700', group: 'compliance' },
    { name: 'Legal', subs: ['Lawyer Fees', 'Audit', 'Consulting', 'Accounting'], icon: Landmark, color: 'bg-stone-100 border-stone-300 text-stone-700', group: 'compliance' },
    { name: 'Fines', subs: ['Traffic Fine', 'Late Fees', 'Regulatory Penalty'], icon: AlertTriangle, color: 'bg-red-100 border-red-300 text-red-700', group: 'compliance' },
    // iter433 — VAT settlement to ZATCA. Posts Dr VAT Payable / Cr Cash/Bank.
    { name: 'VAT Settlement', subs: ['Monthly VAT Payment', 'Quarterly VAT Payment', 'Annual VAT Payment'], icon: Landmark, color: 'bg-purple-100 border-purple-300 text-purple-700', group: 'compliance' },
    // iter442d — Marketing & advertising
    { name: 'Marketing', subs: ['Social Media Ads', 'Print Ads', 'Influencers', 'SEO'], icon: Megaphone, color: 'bg-fuchsia-100 border-fuchsia-300 text-fuchsia-700', group: 'marketing' },
    { name: 'Printing', subs: ['Business Cards', 'Banners', 'Brochures', 'Photocopy'], icon: Printer, color: 'bg-orange-100 border-orange-300 text-orange-700', group: 'marketing' },
    { name: 'Events', subs: ['Sponsorship', 'Conference', 'Exhibition'], icon: Gift, color: 'bg-pink-100 border-pink-300 text-pink-700', group: 'marketing' },
    { name: 'Packaging', subs: [], icon: Package, color: 'bg-cyan-100 border-cyan-300 text-cyan-700', group: 'marketing' },
    // iter442d — Travel & logistics
    { name: 'Transport', subs: ['Local Transport', 'Taxi', 'Delivery'], icon: Truck, color: 'bg-yellow-100 border-yellow-300 text-yellow-700', group: 'logistics' },
    { name: 'Fuel', subs: ['Petrol', 'Diesel', 'Gas'], icon: Fuel, color: 'bg-amber-100 border-amber-300 text-amber-700', group: 'logistics' },
    { name: 'Courier', subs: ['Aramex', 'DHL', 'SMSA', 'Other'], icon: Truck, color: 'bg-blue-100 border-blue-300 text-blue-700', group: 'logistics' },
    // iter442d — Food & beverages for staff
    { name: 'Food', subs: ['Staff Meals', 'Tea / Coffee', 'Office Beverages'], icon: Tag, color: 'bg-rose-100 border-rose-300 text-rose-700', group: 'employee' },
    // iter442d — Hospitality / client entertainment / gifts
    { name: 'Hospitality', subs: ['Client Lunch / Dinner', 'Office Gifts', 'Welcoming Guests'], icon: HeartHandshake, color: 'bg-purple-100 border-purple-300 text-purple-700', group: 'marketing' },
    // iter442d — Donations & charity
    { name: 'Donations', subs: ['Charity', 'Zakat (Charitable)', 'Sponsorship'], icon: Gift, color: 'bg-green-100 border-green-300 text-green-700', group: 'compliance' },
    { name: 'Other', subs: [], icon: MoreHorizontal, color: 'bg-muted border-border text-muted-foreground', group: 'other' },
  ];

  // Categories that should show employee selector instead of supplier
  const employeeLinkedCats = ['Salary', 'Tickets', 'ID Card', 'Government'];

  // iter428 — Group main categories under user-friendly section headings
  // so the picker reads as Main → Sub instead of one long flat row of
  // chips. Anything not listed here lands in "Other / Custom" at the end.
  const CATEGORY_GROUPS = [
    { key: 'operations',  label: 'Operations & Property',     cats: ['Rent', 'Utilities', 'Maintenance', 'Cleaning', 'Vehicle', 'Assets', 'Office Equipment', 'Waste Management', 'Security'] },
    { key: 'employee',    label: 'Employee & Payroll',        cats: ['Salary', 'Tickets', 'ID Card', 'Food', 'Travel', 'Training'] },
    { key: 'compliance',  label: 'Compliance & Government',   cats: ['Government', 'Subscription Fees', 'Legal', 'Insurance', 'Fines', 'VAT Settlement', 'Donations'] },
    { key: 'financial',   label: 'Financial & Suppliers',     cats: ['Bank Fees', 'Loan Repayment', 'Supplier', 'Agent Fee', 'Software'] },
    { key: 'marketing',   label: 'Marketing & Sales',         cats: ['Marketing', 'Printing', 'Events', 'Hospitality', 'Packaging'] },
    { key: 'logistics',   label: 'Travel & Logistics',        cats: ['Transport', 'Fuel', 'Courier'] },
  ];

  useEffect(() => { fetchData(); }, [dateRange, activeBackendFilters, searchFilters, pageSize, branchSel, dailyView]);

  // H3 — realtime refresh: refetch the current filtered page when an expense
  // (incl. credit purchases / bills) changes anywhere. Preserves all filters +
  // page by re-running the SAME fetchData(currentPage).
  useRealtimeRefresh(['new_expense', 'expense_updated', 'expense_deleted'], () => fetchData(currentPage));

  // Lost-payment audit banner (same scan as SuppliersPage): unmatched
  // OUTGOING bank txns that are plausibly booked-but-unlinked expenses.
  useEffect(() => {
    api.get('/recon-cockpit/unmatched', { params: { kind: 'debit', limit: 200, banner: 1, from_date: new Date(Date.now() - 60 * 864e5).toISOString().slice(0, 10) } })
      .then((r) => {
        const rows = r.data?.rows || [];
        const total = Number(r.data?.total || rows.length);
        const total_amount = rows.reduce((s, x) => s + Number(x.amount || 0), 0);
        const likelySupplier = rows.filter((x) => {
          const c = x.best || {};
          const isSup = (c.type === 'supplier_payment') || (c.payee_type === 'supplier');
          const desc = String(x.description || '');
          const looksLikeMerchant = /[A-Za-z\u0600-\u06FF]{3,}/.test(desc) && !/^\s*(TXN|REF|VISA|MADA|SPAN|POS|ATM)\b/i.test(desc);
          return isSup || looksLikeMerchant;
        });
        setLostPaymentsScan({
          total,
          total_amount,
          likely_supplier: likelySupplier.length,
          likely_amount: likelySupplier.reduce((s, x) => s + Number(x.amount || 0), 0),
        });
      })
      .catch(() => setLostPaymentsScan(null));
  }, []);

  // iter434 — Pre-fill the Add Expense form when arriving with `?preset=vat_settlement`
  // (clicked from the VAT Liability widget on Tax Submissions page).
  useEffect(() => {
    if (searchParams.get('preset') !== 'vat_settlement') return;
    const amt = searchParams.get('amount') || '';
    const desc = searchParams.get('description') || '';
    setFormData(fd => ({
      ...fd,
      category: 'VAT Settlement',
      sub_category: 'Monthly VAT Payment',
      amount: amt,
      description: desc,
      vat_enabled: false,        // VAT Settlement is NOT itself VATable
      vat_inclusive: false,
      payment_mode: 'bank',
    }));
    // Strip the query so a refresh doesn't re-trigger.
    const sp = new URLSearchParams(searchParams);
    sp.delete('preset');
    sp.delete('amount');
    sp.delete('description');
    setSearchParams(sp, { replace: true });
    toast.success('VAT Settlement form pre-filled — review and save');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter431 — Focus / pulse-highlight a row when arriving with ?focus=<id>.
  // Auto-expand the day-group that owns the row, then scroll & pulse it.
  useEffect(() => {
    if (!urlFocus || !expenses?.length) return;
    const target = expenses.find(e => e.id === urlFocus);
    if (!target) return;
    const dateKey = target.date ? safeFormat(target.date, 'yyyy-MM-dd', 'unknown') : 'unknown';
    setExpandedDates(prev => ({ ...prev, [dateKey]: true }));
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="expense-detail-${urlFocus}"]`);
      if (el) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* noop */ }
        el.classList.add('row-focus-pulse');
        setTimeout(() => {
          el.classList.remove('row-focus-pulse');
          const sp = new URLSearchParams(searchParams);
          sp.delete('focus');
          setSearchParams(sp, { replace: true });
        }, 2800);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFocus, expenses]);

  // iter818 — Live pre-save duplicate guard. As the user fills in amount/date/
  // branch on the Add/Edit Expense form, check for a same-day + same-amount +
  // same-branch expense (ANY category) and warn inline BEFORE they save, so
  // cross-category doubles (e.g. "Visa Fee" vs "id card") never get created.
  useEffect(() => {
    if (!showAddDialog) { setLiveDup(null); return; }
    const amt = parseFloat(formData.amount);
    if (!formData.branch_id || !formData.date || !Number.isFinite(amt) || amt <= 0) { setLiveDup(null); return; }
    const timer = setTimeout(async () => {
      try {
        const p = new URLSearchParams({
          branch_id: formData.branch_id, amount: String(amt), date: formData.date,
          category: formData.sub_category || formData.category || '',
          payment_mode: formData.payment_mode || '',
          invoice_number: formData.invoice_number || '',
          supplier_id: formData.supplier_id || '', employee_id: formData.employee_id || '',
          exclude_id: editExpense?.id || '',
        });
        const r = await api.get(`/expenses/check-duplicate?${p.toString()}`);
        setLiveDup(r.data || null);
      } catch { setLiveDup(null); }
    }, 500);
    return () => clearTimeout(timer);
  }, [showAddDialog, formData.amount, formData.date, formData.branch_id, formData.category, formData.sub_category, formData.payment_mode, formData.invoice_number, formData.supplier_id, formData.employee_id, editExpense]);


  const fetchData = async (page = 1) => {
    try {
      // Use Zustand for branches
      fetchBranches();
      let expUrl = dailyView ? `/expenses?page=1&limit=2000` : `/expenses?page=${page}&limit=${pageSize}`;
      // Date range: AdvancedSearch date takes priority over date presets
      const effectiveStart = searchFilters?.date?.start || dateRange?.start;
      const effectiveEnd = searchFilters?.date?.end || dateRange?.end;
      if (effectiveStart) expUrl += `&start_date=${effectiveStart}`;
      if (effectiveEnd) expUrl += `&end_date=${effectiveEnd}`;
      if (activeBackendFilters.branch_id) {
        expUrl += `&branch_id=${activeBackendFilters.branch_id}`;
      }
      if (branchSel.length) {
        expUrl += `&branch_ids=${branchSel.join(',')}`;
      }
      if (activeBackendFilters.category) {
        expUrl += `&category=${encodeURIComponent(activeBackendFilters.category)}`;
      }
      if (activeBackendFilters.payment_mode) {
        expUrl += `&payment_mode=${activeBackendFilters.payment_mode}`;
      }
      if (activeBackendFilters.status) {
        expUrl += `&status=${activeBackendFilters.status}`;
      }
      const [eR, sR, cR, rR, empR, treeR, bsR] = await Promise.allSettled([api.get(expUrl), api.get('/suppliers'), api.get('/categories?category_type=expense'), api.get('/recurring-expenses'), api.get('/employees'), api.get('/accounting/expense-category-tree'), api.get('/recurring-expenses/branch-summary')]);
      if (eR.status === 'fulfilled') {
        const expData = eR.value.data;
        setExpenses(expData.data || expData || []);
        setCurrentPage(expData.page || 1);
        setTotalPages(expData.pages || 1);
        setTotalRecords(expData.total || 0);
        setServerTotals({
          total_amount: expData.total_amount || 0,
          cash_total: expData.cash_total || 0,
          bank_total: expData.bank_total || 0,
          credit_total: expData.credit_total || 0,
          vat_total: expData.vat_total || 0,
          branch_breakdown: expData.branch_breakdown || {},
          classified_totals: expData.classified_totals || null,
        });
      }
      if (sR.status === 'fulfilled') setSuppliers(sR.value.data || []);
      if (cR.status === 'fulfilled') setCategories(cR.value.data || []);
      if (rR.status === 'fulfilled') setRecurringExpenses(Array.isArray(rR.value.data) ? rR.value.data : []);
      if (bsR && bsR.status === 'fulfilled') setBranchSummary(bsR.value.data);
      if (empR.status === 'fulfilled') setEmployees(Array.isArray(empR.value.data) ? empR.value.data : []);
      if (treeR.status === 'fulfilled') setCategoryTree(treeR.value.data?.tree || []);
    } catch { toast.error('Failed'); } finally { setLoading(false); }
  };

  // Category tree sourced from `/accounting/expense-category-tree` — merges
  // built-in defaults + tenant Category→Account mappings + subcategories.
  // Each node = { name, raw, account_name, account_id, account_code, subcategories[] }
  // The in-component `defaultCats` below is kept only to supply icons/colors
  // for the built-in categories — everything else comes from the backend.
  const findCatNode = (mainCat) => {
    if (!mainCat) return null;
    const k = mainCat.toLowerCase();
    return categoryTree.find(c => (c.raw || c.name || '').toLowerCase() === k || (c.name || '').toLowerCase() === k) || null;
  };
  const mainCats = (() => {
    // Prefer backend tree; fall back to defaultCats + custom categories when
    // tree hasn't loaded yet (first render). Dedupe case-insensitively.
    const fromTree = categoryTree.map(c => c.name);
    const fromDefaults = defaultCats.map(c => c.name);
    const fromCustom = categories.filter(c => !c.parent_id).map(c => c.name);
    const merged = [...fromTree, ...fromDefaults, ...fromCustom];
    const seen = new Set();
    return merged.filter(n => {
      const k = (n || '').toLowerCase();
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  })();
  const getSubCats = (mainCat) => {
    if (!mainCat) return [];
    const lc = mainCat.toLowerCase();
    const treeNode = findCatNode(mainCat);
    const treeSubs = (treeNode?.subcategories || []).map(s => s.name);
    // iter536 — also match by name match on `raw` to handle case/spacing
    // mismatches between the backend tree and the chip label.
    const defaultSubs = (defaultCats.find(c => c.name.toLowerCase() === lc)?.subs) || [];
    const customSubs = categories.filter(c => c.parent_id).filter(c => {
      const parent = categories.find(p => p.id === c.parent_id);
      return (parent?.name || '').toLowerCase() === lc;
    }).map(c => c.name);
    return [...treeSubs, ...defaultSubs, ...customSubs].filter((v, i, a) => v && a.indexOf(v) === i);
  };

  // iter536 — Live filter text for the category chip picker.
  // Filters both group labels and chips when the search box is non-empty.
  const [catSearch, setCatSearch] = useState('');
  const { match: catMatch, accountOf: catAccountOf } = useCategorySearch();

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Advance Payment (pass-through) — reuses the existing
    // pass_through_advances structure; linked to an Expense/Purchase later.
    if (formData.is_advance) {
      if (!formData.advance_person_id || !formData.amount) { toast.error('Pick the employee/partner and amount'); return; }
      try {
        await api.post('/pass-through-advances', {
          person_type: formData.advance_person_type,
          person_id: formData.advance_person_id,
          payment_mode: formData.payment_mode === 'bank' ? 'bank' : 'cash',
          amount: parseFloat(formData.amount),
          date: formData.date,
          paying_branch_id: formData.branch_id || null,
          description: formData.description || 'Advance Payment',
        });
        toast.success('Advance recorded — link it to an expense/purchase from Pass-Through Advances');
        setShowAddDialog(false);
        setFormData(prev => ({ ...prev, is_advance: false, advance_person_id: '', amount: '', description: '' }));
        fetchData();
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record advance'); }
      return;
    }
    if (!formData.category || !formData.amount) { toast.error('Select category and amount'); return; }
    // iter927 — Mandatory Branch guardrail: an expense must be tied to a branch.
    if (!formData.branch_id) { toast.error('Please select a Branch before saving'); return; }

    // Check for duplicates before saving
    const amt = parseFloat(formData.amount);
    if (formData.branch_id && amt > 0) {
      try {
        const dupParams = new URLSearchParams({
          branch_id: formData.branch_id || '',
          amount: String(amt),
          date: formData.date,
          category: formData.sub_category || formData.category || '',
          payment_mode: formData.payment_mode || '',
          invoice_number: formData.invoice_number || '',
          supplier_id: formData.supplier_id || '',
          employee_id: formData.employee_id || '',
          exclude_id: editExpense?.id || '',
        });
        const checkRes = await api.get(`/expenses/check-duplicate?${dupParams.toString()}`);
        if (checkRes.data?.has_duplicate || checkRes.data?.cross_category_count > 0) {
          const rows = [...(checkRes.data.existing || []), ...(checkRes.data.cross_category_existing || [])];
          setDuplicateCount((checkRes.data.count || 0) + (checkRes.data.cross_category_count || 0));
          setDuplicateExisting(rows);
          setShowDuplicateWarning(true);
          return;
        }
      } catch { /* proceed if check fails */ }
    }

    await submitExpense();
  };

  const submitExpense = async () => {
    try {
      // Auto-add employee name to description for employee-related expenses
      let description = formData.description || '';
      if (formData.employee_id && ['Salary', 'Tickets', 'ID Card'].includes(formData.category)) {
        const emp = employees.find(e => e.id === formData.employee_id);
        if (emp && !description.toLowerCase().includes(emp.name.toLowerCase())) {
          description = description ? `${emp.name} - ${description}` : emp.name;
        }
      }

      // iter526 — If this is a Fine-category expense AND the user filled
      // in any fine attribution (vehicle/employee/violation_date), divert
      // the submission to POST /api/fines so a Fine record is created.
      // The Fine endpoint already auto-creates the linked expense row +
      // salary deduction (when deduct_from_salary=true) — calling both
      // would double-book, so this single call replaces /expenses.
      const hasFineAttribution = isFineCategory && (
        formData.employee_id || formData.fine_vehicle_id || formData.fine_plate_number ||
        formData.fine_violation_date || formData.fine_deduct_from_salary || formData.fine_department
      );
      if (hasFineAttribution) {
        const finePayload = {
          fine_type: (formData.sub_category || formData.category || 'traffic').toLowerCase(),
          department: formData.fine_department || formData.sub_category || 'Fines',
          description: description || formData.category,
          amount: parseFloat(formData.amount),
          branch_id: formData.branch_id || null,
          employee_id: formData.employee_id || null,
          vehicle_id: formData.fine_vehicle_id || null,
          plate_number: formData.fine_plate_number || null,
          violation_date: formData.fine_violation_date ? `${formData.fine_violation_date}T00:00:00` : null,
          fine_date: `${formData.date}T00:00:00`,
          deduct_from_salary: !!formData.fine_deduct_from_salary,
          monthly_deduction: parseFloat(formData.fine_monthly_deduction || 0) || 0,
          notes: formData.notes || null,
          bill_number: formData.invoice_number || null,
        };
        await api.post('/fines', finePayload);
        toast.success('Fine recorded — linked Expense & salary deduction auto-created');
        setFormData({ category: '', sub_category: '', description: '', amount: '', payment_mode: 'cash', branch_id: '', expense_for_branch_id: '', supplier_id: '', employee_id: '', date: localToday(), notes: '', invoice_number: '', vat_inclusive: true, vat_rate: 15, vat_enabled: true, fine_vehicle_id: '', fine_plate_number: '', fine_violation_date: '', fine_department: '', fine_deduct_from_salary: false, fine_monthly_deduction: '' });
        setShowAddDialog(false);
        fetchData();
        return;
      }

      // iter442e — If the user picked a bank-statement txn, link it
      // BEFORE creating the expense via the normal /expenses POST. We
      // use the bulk-from-stmt-txns endpoint with a single ref — this
      // creates the expense AND marks the statement txn as matched in
      // one shot. Falls back to the normal path otherwise.
      //
      // iter724 — Same fix as SupplierPaymentsPage: pass
      // `paying_bank_account_id` (was missing → backend HTTP-400'd into
      // the skipped list, but we used to toast success regardless),
      // and verify `created_count > 0` before claiming success.
      if (formData.payment_mode === 'bank' && pickedBankTxn) {
        const isSupplier = !!formData.supplier_id && !employeeLinkedCats.includes(formData.category);
        const res = await api.post('/banking/bank-payments/bulk-from-stmt-txns', {
          txn_refs: [{ statement_id: pickedBankTxn.statement_id, txn_index: pickedBankTxn.txn_index }],
          payee_type: isSupplier ? 'supplier' : 'expense_category',
          payee_id: isSupplier ? formData.supplier_id : null,
          category: !isSupplier ? (formData.sub_category || formData.category) : null,
          sub_category: !isSupplier && formData.sub_category ? formData.sub_category : null,
          paying_branch_id: formData.branch_id || null,
          // iter724 — REQUIRED for bank payment_mode.
          paying_bank_account_id: pickedBankTxn.bank_account_id || null,
          description: description || formData.category,
        });
        const createdCount = Number(res?.data?.created_count || 0);
        if (createdCount === 0) {
          const reason = res?.data?.skipped?.[0]?.reason || 'unknown reason';
          toast.error(`Expense NOT recorded — ${reason}`);
          return;
        }
        toast.success('Expense added & linked to bank statement');
        setFormData({ category: '', sub_category: '', description: '', amount: '', payment_mode: 'cash', branch_id: '', expense_for_branch_id: '', supplier_id: '', employee_id: '', date: localToday(), notes: '', invoice_number: '', vat_inclusive: true, vat_rate: 15, vat_enabled: true });
        setPickedBankTxn(null);
        setShowAddDialog(false);
        fetchData();
        return;
      }
      const _vatErr = vatError(formData);
      if (_vatErr) { toast.error(_vatErr); return; }
      await api.post('/expenses', {
        ...formData, description,
        category: formData.sub_category || formData.category,
        sub_category: formData.sub_category ? formData.category : null,
        amount: parseFloat(formData.amount),
        ...vatPayload(formData),
        branch_id: formData.branch_id || null, supplier_id: formData.supplier_id || null,
        expense_for_branch_id: formData.expense_for_branch_id || null,
        date: localDateTimeStamp(formData.date),
        notes: formData.employee_id ? `Employee: ${employees.find(e => e.id === formData.employee_id)?.name || ''}` : formData.notes,
      });
      toast.success('Expense added');
      setFormData({ category: '', sub_category: '', description: '', amount: '', payment_mode: 'cash', branch_id: '', expense_for_branch_id: '', supplier_id: '', employee_id: '', date: localToday(), notes: '', invoice_number: '', vat_inclusive: true, vat_rate: 15, vat_enabled: true });
      setShowAddDialog(false);  // iter442 — close the modal after a successful add
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddCat = async () => {
    if (!newCat.trim()) return;
    try { await api.post('/categories', { name: newCat.trim(), type: 'expense' }); toast.success('Category added'); setNewCat(''); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const handleAddSubCat = async () => {
    if (!newSubCat.name.trim() || !newSubCat.parent) return;
    const parent = categories.find(c => c.name === newSubCat.parent && !c.parent_id);
    try { await api.post('/categories', { name: newSubCat.name.trim(), type: 'expense', parent_id: parent?.id || null }); toast.success('Sub-category added'); setNewSubCat({ name: '', parent: '' }); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const openEditExpense = (exp) => {
    setEditExpense(exp);
    setEditData({
      description: exp.description || '',
      category: exp.category || '',
      amount: exp.amount || 0,
      payment_mode: exp.payment_mode || 'cash',
      vat_rate: exp.vat_rate || 0,
      vat_inclusive: exp.vat_inclusive || false,
      invoice_number: exp.invoice_number || '',
    });
  };

  const saveEditExpense = async () => {
    if (!editExpense) return;
    try {
      await api.put(`/expenses/${editExpense.id}`, editData);
      toast.success('Expense updated');
      setEditExpense(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  // iter404 — Inline-edit a single field on an expense row without
  // opening the full edit dialog. Saves a focused PUT with just the
  // changed field. Used by the click-to-edit Amount / Description /
  // Invoice cells in the expense detail rows.
  const inlineUpdateExpense = async (expId, patch) => {
    try {
      await api.put(`/expenses/${expId}`, patch);
      toast.success('Saved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
      throw err;
    }
  };

  const toggleSelectExpense = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    const visibleIds = expenses.map(e => e.id);
    const allSelected = visibleIds.every(id => selectedIds.has(id));
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(visibleIds));
  };

  const applyBulkVat = async () => {
    const hasSelection = selectedIds.size > 0;
    const hasFilters = bulkFilterSupplier || bulkFilterStartDate || bulkFilterEndDate;
    if (!hasSelection && !hasFilters) { toast.error('Select expenses or set filters'); return; }
    try {
      const payload = {
        expense_ids: hasSelection ? Array.from(selectedIds) : [],
        vat_rate: bulkVatRate,
        vat_inclusive: bulkVatInclusive,
        skip_existing_vat: bulkSkipExisting,
      };
      if (!hasSelection && hasFilters) {
        if (bulkFilterSupplier) payload.filter_supplier_id = bulkFilterSupplier;
        if (bulkFilterStartDate) payload.filter_start_date = bulkFilterStartDate;
        if (bulkFilterEndDate) payload.filter_end_date = bulkFilterEndDate;
      }
      const res = await api.post('/expenses/bulk-vat-update', payload);
      toast.success(`Updated: ${res.data.updated}, Skipped (already had VAT): ${res.data.skipped}`);
      setBulkVatMode(false);
      setSelectedIds(new Set());
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk update failed'); }
  };


  const handleDeleteCat = async (catId, catName) => {
    if (!catId) { toast.error('Cannot delete default categories'); return; }
    try { 
      await api.delete(`/categories/${catId}`); 
      toast.success(`Category "${catName}" deleted`); 
      fetchData(); 
    } catch { toast.error('Failed to delete category'); }
  };

  const handleRefundSubmit = async (e) => {
    e.preventDefault();
    if (!refundData.amount || parseFloat(refundData.amount) <= 0) { toast.error('Enter a valid refund amount'); return; }
    try {
      await api.post('/expense-refunds', {
        ...refundData,
        amount: parseFloat(refundData.amount),
        date: localDateTimeStamp(refundData.date),
      });
      toast.success(`Refund of ${cur()} ${refundData.amount} recorded`);
      setShowRefundDialog(false);
      setRefundData({ amount: '', reason: '', refund_mode: 'cash', category: 'Refund', branch_id: '', date: localToday() });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  const filtered = applySearchFilters(expenses, searchFilters);
  const totalExp = serverTotals.total_amount || filtered.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {showHeader && <FinancialSummaryHeader />}
        {/* Lost-payment audit banner — mirrors SuppliersPage. Fires when
            unmatched outgoing bank txns look like expenses/payments that
            were never linked. Suppressed when zero. */}
        {lostPaymentsScan && lostPaymentsScan.total > 0 && (
          <Card className="border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50/40" data-testid="lost-payments-banner">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="rounded-full bg-amber-100 p-2 mt-0.5">
                  <AlertTriangle className="text-amber-700" size={18} />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-amber-900 text-sm">
                    {lostPaymentsScan.total} unmatched outgoing bank transaction{lostPaymentsScan.total === 1 ? '' : 's'} in the last 60 days — possibly expenses or payments never linked
                  </div>
                  <div className="text-xs text-amber-800 mt-0.5">
                    Worth <strong>{cur()} {Math.round(lostPaymentsScan.total_amount).toLocaleString()}</strong> total
                    {lostPaymentsScan.likely_supplier > 0 && (
                      <> · <strong>{lostPaymentsScan.likely_supplier}</strong> match supplier-payment patterns ({cur()} {Math.round(lostPaymentsScan.likely_amount).toLocaleString()})</>
                    )}. Re-pair them in Recon Cockpit.
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                className="shrink-0 rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => navigate('/bank-statements/reconcile?kind=debit')}
                data-testid="lost-payments-cta"
              >
                Review in Recon Cockpit
              </Button>
            </CardContent>
          </Card>
        )}
        {/* Branch-wise Monthly Expenses Summary */}
        {showHeader && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-red-50 to-amber-50 dark:from-red-900/20 dark:to-amber-900/20 card-enter" data-testid="expenses-branch-summary">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingDown size={18} className="text-error" />
                <h3 className="font-semibold text-sm">{branchMonthlyExpenses.month} - Branch Expenses</h3>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Total for Period</p>
                <p className="text-xl font-bold text-error inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {branchMonthlyExpenses.totalAll.toLocaleString()}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(branchMonthlyExpenses.byBranch).map(([branchName, data]) => {
                const branchId = data.branch_id || branches.find(b => b.name === branchName)?.id || null;
                return (
                <div key={branchName} className="bg-white dark:bg-muted rounded-xl p-4 border">
                  <div className="flex items-center gap-1.5 mb-2.5">
                    <Store size={14} className="text-error" />
                    <span className="text-xs font-medium truncate">
                      {branchId ? <BranchLink branchId={branchId} name={branchName} /> : branchName}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-error inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {data.total.toLocaleString()}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {data.cash > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-success/20 text-success rounded">Cash: {data.cash.toLocaleString()}</span>}
                    {data.bank > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-info/20 text-info rounded">Bank: {data.bank.toLocaleString()}</span>}
                    {data.credit > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-warning/20 text-warning rounded">Credit: {data.credit.toLocaleString()}</span>}
                  </div>
                </div>
              );})}
              {Object.keys(branchMonthlyExpenses.byBranch).length === 0 && (
                <div className="col-span-full text-center py-3 text-sm text-muted-foreground">No expenses recorded this month</div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
          <div className="min-w-0 flex-1">
            <ReportHeader icon={Receipt} title={<span data-testid="expenses-page-title">{t('expenses_title')}</span>} subtitle={t('expenses_subtitle')} testId="expenses-header" />
            <button
              type="button"
              onClick={() => navigate('/banking-hub')}
              data-testid="expenses-bank-payments-hint"
              className={`${showHint ? 'inline-flex' : 'hidden'} items-center gap-1.5 mt-1.5 text-[11px] text-info hover:text-info/80 hover:underline transition-colors`}
              title="Outgoing bank transfers, supplier payments and statement-linked txns live in the Banking Hub. Use ‘Convert to Expense’ to bring them here."
            >
              <Banknote size={12} />
              <span>Looking for bank transfers? They live in Banking Hub — convert or delete from there.</span>
            </button>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <PageTour storageKey="tour_expenses" steps={[
              { target: '[data-testid="add-expense-btn"]', title: 'Add Expense', content: 'Record expenses with category, amount, payment method, and optional invoice upload.' },
              { target: '[data-testid="add-refund-btn"]', title: 'Refunds', content: 'Record refunds for returned items or cancelled transactions.' },
              { target: '[data-testid="expenses-branch-summary-2"]', title: 'Branch Summary', content: 'Monthly expense breakdown by branch showing cash, bank, and credit totals.' },
            ]} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-xl h-9 text-xs" data-testid="expenses-view-menu">
                  <SlidersHorizontal size={14} className="mr-1" /> View <ChevronDown size={14} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="expenses-view-menu-content">
                <DropdownMenuLabel className="text-xs">Customize this page</DropdownMenuLabel>
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); toggleHeader(); }} data-testid="expenses-toggle-header-btn">
                  {showHeader ? <ChevronUp size={14} className="mr-2" /> : <ChevronDown size={14} className="mr-2" />}
                  {showHeader ? 'Hide summary panel' : 'Show summary panel'}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); toggleHint(); }} data-testid="expenses-toggle-hint-btn">
                  {showHint ? <ChevronUp size={14} className="mr-2" /> : <ChevronDown size={14} className="mr-2" />}
                  {showHint ? 'Hide bank-transfer tip' : 'Show bank-transfer tip'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-xl h-9 text-xs" data-testid="expenses-export-menu">
                  <Download size={14} className="mr-1" /> Export <ChevronDown size={14} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-2 w-64" data-testid="expenses-export-menu-content">
                <DropdownMenuLabel className="text-xs">Export expenses</DropdownMenuLabel>
                <label
                  className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none rounded-lg px-1 py-1.5 hover:text-foreground transition-colors"
                  data-testid="expenses-export-pivot-toggle"
                  title="Include a second sheet with Branch × Payment-Mode totals"
                >
                  <input
                    type="checkbox"
                    checked={includePivot}
                    onChange={(e) => setIncludePivot(e.target.checked)}
                    className="accent-primary h-3 w-3"
                    data-testid="expenses-export-pivot-checkbox"
                  />
                  + Pivot sheet
                </label>
                <div className="pt-1 flex flex-wrap gap-1">
                  <ExportButtons branchIds={branchSel}
                    dataType="expenses"
                    startDate={searchFilters?.date?.start || dateRange?.start}
                    endDate={searchFilters?.date?.end || dateRange?.end}
                    filters={{
                      branch_id: activeBackendFilters.branch_id || null,
                      branch_ids: (branchSel && branchSel.length) ? branchSel.join(',') : null,
                      payment_method: activeBackendFilters.payment_mode || null,
                      category: activeBackendFilters.category || null,
                      include_pivot: includePivot || null,
                    }}
                  />
                  <PDFExportButton reportType="expenses" label="Branded PDF" />
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            {isAdmin && (
              <DuplicateExpensesScanner
                startDate={searchFilters?.date?.start || dateRange?.start || localMonthStart()}
                endDate={searchFilters?.date?.end || dateRange?.end || localToday()}
                branchId={activeBackendFilters.branch_id || null}
                periodLabel="selected period"
                onDeleted={() => { fetchData && fetchData(currentPage); }}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-xl h-9 text-xs" data-testid="expenses-more-actions">
                  <MoreHorizontal size={14} className="mr-1" /> More Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onSelect={() => navigate('/supplier-payments')} data-testid="pay-supplier-credit-btn">
                  <CreditCard size={14} className="mr-2 text-credit" /> Pay Supplier Credit
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setShowRefundDialog(true)} data-testid="add-refund-btn-2">
                  <RotateCcw size={14} className="mr-2 text-error" /> Refund
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setShowWhatsApp(true)} data-testid="expenses-whatsapp-btn">
                  <MessageCircle size={14} className="mr-2" /> WhatsApp
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem
                    data-testid="expenses-recover-hidden-btn"
                    onSelect={async () => {
                      if (!window.confirm('Recover hidden bank-statement expenses?\n\nThis scans for expenses that have a parent bank statement but a missing tenant tag (a one-time bug from earlier versions) and re-attaches them so they appear on this page.\n\nSafe and idempotent.')) return;
                      try {
                        const r = await api.post('/expenses/backfill-tenant-from-source');
                        const fx = r.data?.fixed ?? 0;
                        if (fx > 0) { toast.success(`Recovered ${fx} hidden expense${fx === 1 ? '' : 's'} — refreshing list…`); fetchData(); }
                        else { toast.info(r.data?.message || 'Nothing to recover — no hidden expenses found.'); }
                      } catch (e) { toast.error(e.response?.data?.detail || 'Recovery failed'); }
                    }}
                  >
                    <RotateCcw size={14} className="mr-2 text-violet-700" /> Recover hidden
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem onSelect={() => setShowCatManager(true)}>
                    <Settings2 size={14} className="mr-2" /> {t('category')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  data-testid="expenses-help-btn"
                  onSelect={() => { Object.keys(localStorage).filter(k => k.includes('expenses_tour')).forEach(k => localStorage.removeItem(k)); localStorage.setItem('ssc_expenses_tour_enabled', 'true'); window.location.reload(); }}
                >
                  <HelpCircle size={14} className="mr-2" /> Tour
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Date Quick Filter + shared Branch filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <DateQuickFilter onFilterChange={(range) => setDateRange(range)} />
          <BranchMultiSelect value={branchSel} onChange={setBranchSel} branches={branches} testid="expenses-branch-multiselect" />
          <div className="inline-flex items-center rounded-xl border border-border bg-muted/40 p-0.5 h-9" role="group" data-testid="expenses-view-segmented">
            {[
              { v: false, label: 'Flat', testid: 'expenses-view-flat', title: 'List each transaction on its own row' },
              { v: true, label: 'Daily', testid: 'expenses-daily-view-toggle', title: 'Group expenses by day (collapsible)' },
            ].map((s) => (
              <button
                key={String(s.v)}
                type="button"
                onClick={() => setDailyView(s.v)}
                data-testid={s.testid}
                title={s.title}
                aria-pressed={dailyView === s.v}
                className={`px-3 h-8 rounded-lg text-xs font-medium transition-colors ${dailyView === s.v ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {urlDateFilter && (
          <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-orange-50 rounded-lg border border-orange-200" data-testid="date-filter-banner">
            <CalendarDays size={14} className="text-orange-600" />
            <span className="text-xs font-medium text-orange-700">Filtered by date: {urlDateFilter}</span>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-orange-600 hover:text-orange-800 ml-auto"
              onClick={() => window.location.href = '/expenses'} data-testid="clear-date-filter">
              Clear filter
            </Button>
          </div>
        )}
        {/* Advanced Search */}
        <AdvancedSearch 
          onSearch={(f) => {
            if (urlDateFilter && !f.date) {
              setSearchFilters({ ...f, date: { start: urlDateFilter, end: urlDateFilter } });
            } else {
              setSearchFilters(f);
            }
            // Pass server-filterable fields to backend
            const newBackendFilters = {};
            if (f.branch_id && f.branch_id !== 'all') newBackendFilters.branch_id = f.branch_id;
            else if (urlBranchFilter && !('branch_id' in f)) newBackendFilters.branch_id = urlBranchFilter;  // keep deep-link branch
            if (f.category && f.category !== 'all') newBackendFilters.category = f.category;
            if (f.payment_mode && f.payment_mode !== 'all') newBackendFilters.payment_mode = f.payment_mode;
            if (f.status && f.status !== 'all') newBackendFilters.status = f.status;
            setActiveBackendFilters(newBackendFilters);
          }}
          config={{
            searchFields: ['description', 'notes', 'invoice_number', 'category', 'vendor', 'supplier_name', 'paid_to', 'source', 'source_group_key'],
            placeholder: 'Search by description, invoice #, category, vendor, supplier…',
            filters: [
              { 
                key: 'category', 
                label: 'Category', 
                type: 'select', 
                options: mainCats.map(c => ({ value: c, label: tc(c) }))
              },
              {
                key: 'status',
                label: 'Status',
                type: 'select',
                options: [
                  { value: 'paid', label: 'Paid' },
                  { value: 'unpaid', label: 'Unpaid' },
                  { value: 'partial', label: 'Partial' },
                  { value: 'pending', label: 'Pending' },
                ],
              },
              { 
                key: 'payment_mode', 
                label: 'Payment', 
                type: 'select', 
                options: [
                  { value: 'cash', label: 'Cash' },
                  { value: 'bank', label: 'Bank' },
                  { value: 'credit', label: 'Credit' }
                ]
              },
              // iter521 — Source filter so users can find expenses
              // created via the bank-statement smart-group flow vs
              // manually entered.
              {
                key: 'source',
                label: 'Source',
                type: 'select',
                options: [
                  { value: '__manual__', label: 'Manual entry' },
                  { value: 'bank_statement_smart_group', label: 'Bank statement (smart group)' },
                  { value: 'bank_statement_smart_group_rule', label: 'Bank statement (rule)' },
                ],
              },
              { key: 'amount', label: 'Amount', type: 'range' },
            ]
          }}
        />

        <Tabs defaultValue="list">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <TabsList>
              <TabsTrigger value="list" data-testid="tab-list">{t('all_expenses')} ({filtered.length})</TabsTrigger>
              <TabsTrigger value="recurring" data-testid="tab-recurring">Recurring</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/scan-invoice')}
                className="rounded-xl border-primary/40 text-primary hover:bg-primary/5"
                data-testid="scan-invoice-qr-btn">
                <ScanLine size={16} className="mr-2" />Scan Invoice QR
              </Button>
              {hasPermission('expenses') && (
                <Button variant="outline" onClick={() => setShowVatReview(true)} className="rounded-xl" data-testid="open-vat-review-btn">
                  Oct VAT review
                </Button>
              )}
              <PurchaseVatReviewDialog open={showVatReview} onClose={() => setShowVatReview(false)} />
              <Button
                onClick={() => setShowAddDialog(true)}
                className="rounded-xl bg-error hover:bg-error/90 text-white shadow-sm"
                data-testid="open-add-expense-dialog">
                <Plus size={16} className="mr-2" />{t('add_expense')}
              </Button>
            </div>
          </div>

          {/* ADD EXPENSE — iter442: same form, now hosted inside a centered modal.
              iter442c: wider modal + larger touch targets for easier data entry. */}
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogContent
              className="sm:max-w-[min(96vw,1180px)] w-[96vw] max-h-[92dvh] overflow-y-auto p-6 sm:p-8"
              id="add-expense-dialog"
              data-testid="add-expense-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl">
                  <Plus size={20} /> {t('add_expense')}
                </DialogTitle>
                <p className="text-xs text-muted-foreground">
                  Pick a category, fill the basics, hit Add. Required fields marked with *
                </p>
              </DialogHeader>
              <div className="pt-2">
                {/* iter474 — Cross-branch hint banner when deep-linked from Inter-Branch Ledger. */}
                {crossBranchHint && (
                  <div className="mb-3 p-2.5 rounded-lg bg-info/10 border border-info/30 text-xs text-info flex items-start gap-2" data-testid="cross-branch-hint-banner">
                    <span className="font-bold">📍 Cross-Branch:</span>
                    <span>
                      Set <strong>Branch</strong> to the one that PAID and use
                      <strong> &ldquo;Expense For (Branch)&rdquo;</strong> below to pick the branch that
                      this expense was incurred FOR. The Inter-Branch Ledger will track it automatically.
                    </span>
                    <button type="button" className="ml-auto text-muted-foreground hover:text-foreground" onClick={() => setCrossBranchHint(false)}>×</button>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* iter442d — Recent categories strip. Shows the 6 most-
                      used categories in the last 30 days as one-click
                      chips so the user can skip the full picker for
                      repeat entries.  Computed live from `expenses` state
                      and reactive to the form state.
                      iter449 — Prepends a "VAT on Bank Charges – Input"
                      pinned quick-add tile (always shown, even when
                      Recent has no entries yet) so accountants can land
                      on this newly-added category in one tap. */}
                  {(() => {
                    const since = Date.now() - 30 * 24 * 60 * 60 * 1000;
                    const counts = {};
                    (expenses || []).forEach(e => {
                      const t = e.date ? new Date(e.date).getTime() : 0;
                      if (!t || t < since) return;
                      const c = (e.category || '').trim();
                      if (!c) return;
                      counts[c] = (counts[c] || 0) + 1;
                    });
                    const recent = Object.entries(counts)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 6)
                      .map(([name, count]) => ({ name, count, pinned: false }));
                    // Pinned quick-adds — always present even when Recent is empty.
                    const PINNED = [
                      { name: 'Vat On Bank Charges', count: null, pinned: true, hint: 'VAT recoverable on bank fees (Input VAT)' },
                    ];
                    // Drop a pinned tile from the recent list if the user
                    // happens to have used it (avoids duplicate chips).
                    const recentFiltered = recent.filter(r => !PINNED.find(p => p.name.toLowerCase() === r.name.toLowerCase()));
                    const tiles = [...PINNED, ...recentFiltered];
                    if (tiles.length === 0) return null;
                    return (
                      <div data-testid="recent-categories-strip" className="-mb-1">
                        <div className="flex items-center gap-1.5 mb-2">
                          <History size={12} className="text-muted-foreground" />
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Quick-adds &amp; recent (last 30 days)</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {tiles.map(r => {
                            const def = defaultCats.find(d => d.name === r.name);
                            const Icon = def?.icon || Tag;
                            const isActive = formData.category === r.name;
                            const isPinned = r.pinned;
                            return (
                              <button
                                type="button"
                                key={r.name}
                                onClick={() => {
                                  setFormData({ ...formData, category: r.name, sub_category: '' });
                                  const el = document.getElementById('add-expense-dialog');
                                  if (el) el.scrollTop = 0;
                                }}
                                title={r.hint || ''}
                                data-testid={`recent-cat-${r.name}`}
                                className={`group flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs transition-all ${
                                  isActive ? 'bg-error text-white border-error shadow-sm scale-[1.02]'
                                  : isPinned ? 'bg-sky-50 text-sky-900 border-sky-300 hover:bg-sky-100 hover:border-sky-400'
                                  : 'bg-violet-50 text-violet-900 border-violet-200 hover:bg-violet-100 hover:border-violet-300'
                                }`}
                              >
                                {isPinned ? <Pin size={11} /> : <Icon size={11} />}
                                <span className="font-medium">{r.name}</span>
                                {r.count != null && (
                                  <span className={`text-[10px] font-mono ${isActive ? 'text-white/80' : 'text-violet-500'}`}>×{r.count}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Label>{t('category')} *</Label>
                      {/* iter536 — Search box for the category picker (user request) */}
                      <div className="relative w-64 max-w-[60%]">
                        <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        <Input
                          value={catSearch}
                          onChange={(e) => setCatSearch(e.target.value)}
                          placeholder="Search category…"
                          className="h-7 pl-7 text-xs"
                          data-testid="expense-category-search"
                        />
                        {catSearch && (
                          <button
                            type="button"
                            onClick={() => setCatSearch('')}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label="Clear"
                            data-testid="expense-category-search-clear"
                          >
                            <X size={11} />
                          </button>
                        )}
                      </div>
                    </div>
                    {/* iter428 — Grouped chip picker. Each section is a
                        "main category" group; chips inside are leaf
                        main-categories (which themselves may have sub
                        categories rendered below once selected).
                        Anything not in CATEGORY_GROUPS lands in "Other / Custom". */}
                    {(() => {
                      const ciFind = (arr, name) => arr.find(x => (x || '').toLowerCase() === (name || '').toLowerCase());
                      const used = new Set();
                      // iter536 — apply search filter to chip names + sub-category names
                      const q = (catSearch || '').trim().toLowerCase();
                      const matchesSearch = (c) => {
                        if (!q) return true;
                        if ((c || '').toLowerCase().includes(q)) return true;
                        // Also include if any sub-category matches
                        const subs = getSubCats(c) || [];
                        // Phase A — spelling variants, linked account name/code, built-in synonyms
                        return subs.some(s => (s || '').toLowerCase().includes(q)) || !!catMatch(c, q);
                      };
                      const sections = CATEGORY_GROUPS.map(g => {
                        const present = g.cats.filter(c => ciFind(mainCats, c)).map(c => ciFind(mainCats, c)).filter(matchesSearch);
                        present.forEach(p => used.add((p || '').toLowerCase()));
                        return { ...g, present };
                      }).filter(s => s.present.length > 0);
                      const leftovers = mainCats.filter(c => !used.has((c || '').toLowerCase())).filter(matchesSearch);
                      if (leftovers.length > 0) {
                        sections.push({ key: 'other', label: 'Other / Custom', present: leftovers });
                      }
                      const renderChip = (c) => {
                        const catDef = defaultCats.find(d => d.name.toLowerCase() === (c || '').toLowerCase());
                        const CatIcon = catDef?.icon || MoreHorizontal;
                        const colorClass = catDef?.color || 'bg-muted border-border text-foreground';
                        const hit = q ? catMatch(c, q) : null;
                        const acc = catAccountOf(c);
                        return (
                          <button key={c} type="button" onClick={() => setFormData({...formData, category: c, sub_category: ''})}
                            className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition-all inline-flex items-center gap-1.5 ${colorClass} ${formData.category === c ? 'ring-2 ring-primary ring-offset-1 scale-105 shadow-md' : 'opacity-80 hover:opacity-100 hover:scale-105'}`}
                            title={acc?.account_name ? `Posts to ${acc.account_code || ''} ${acc.account_name}`.trim() : undefined}
                            data-testid={`cat-btn-${c.toLowerCase().replace(/\s/g, '-')}`}>
                            <CatIcon size={14} />
                            {c}
                            {hit?.text && <span className="text-[10px] font-normal opacity-70" data-testid={`cat-match-${c.toLowerCase().replace(/\s/g, '-')}`}>· {hit.text}</span>}
                          </button>
                        );
                      };
                      return (
                        <div className="space-y-3 mt-2" data-testid="expense-category-grouped-picker">
                          {sections.length === 0 && q && (
                            <div className="text-xs text-muted-foreground italic px-2 py-3 text-center border border-dashed rounded-lg" data-testid="expense-category-search-empty">
                              No category matches "{catSearch}". Try a shorter search or click "Add new" below.
                            </div>
                          )}
                          {sections.map(s => (
                            <div key={s.key} data-testid={`cat-group-${s.key}`}>
                              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5 flex items-center gap-1.5">
                                <span className="w-1 h-3 bg-primary/40 rounded-full" />{s.label}
                                <span className="text-[9px] font-normal text-muted-foreground/70">({s.present.length})</span>
                              </div>
                              <div className="flex gap-2 flex-wrap">
                                {s.present.map(renderChip)}
                              </div>
                              {/* iter536 — preview ALL sub-categories under each visible main when the user is searching, so they can click directly */}
                              {q && s.present.map(c => {
                                const subs = (getSubCats(c) || []).filter(sub => (sub || '').toLowerCase().includes(q));
                                if (subs.length === 0) return null;
                                return (
                                  <div key={`sub-${c}`} className="mt-1.5 ml-3 flex flex-wrap gap-1.5">
                                    {subs.map(sub => (
                                      <button
                                        key={sub}
                                        type="button"
                                        onClick={() => { setFormData({ ...formData, category: c, sub_category: sub }); setCatSearch(''); }}
                                        className="px-2 py-0.5 rounded-md border text-[11px] bg-violet-50 text-violet-900 border-violet-200 hover:bg-violet-100"
                                        data-testid={`sub-cat-pick-${sub}`}
                                      >
                                        {c} › <span className="font-semibold">{sub}</span>
                                      </button>
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  {formData.category && formData.account_override && (
                    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-success/5 border border-success/30 text-xs" data-testid="expense-account-override">
                      <div className="w-1 h-10 bg-success rounded-full flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <span className="text-muted-foreground">Posts to (this expense only):</span>{' '}
                        <span className="font-semibold text-foreground">{formData.account_override}</span>
                      </div>
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, account_override: undefined }))}
                        className="px-2 py-1 rounded-md text-[11px] text-muted-foreground hover:text-foreground" data-testid="expense-clear-override-btn">
                        Reset
                      </button>
                    </div>
                  )}
                  {formData.category && !formData.account_override && (() => {
                    const node = findCatNode(formData.category);
                    if (!node) return null;
                    if (node.account_name) {
                      return (
                        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-info/5 border border-info/20 text-xs" data-testid="expense-account-preview">
                          <div className="w-1 h-10 bg-info rounded-full flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <span className="text-muted-foreground">Posts to account:</span>{' '}
                            <span className="font-semibold text-foreground">{node.account_name}</span>
                            {node.account_code && <span className="ml-1.5 text-[10px] font-mono text-muted-foreground">[{node.account_code}]</span>}
                            {node.is_default && <span className="ml-2 text-[10px] text-muted-foreground italic">(default mapping)</span>}
                          </div>
                          <button
                            type="button"
                            onClick={() => openQuickMap(formData.category)}
                            className="px-2.5 py-1 rounded-md border border-info/40 text-info text-[11px] font-medium hover:bg-info/10 transition-colors flex-shrink-0"
                            data-testid="expense-change-account-btn"
                          >
                            <LinkIcon size={11} className="inline mr-1 -mt-0.5" />Change
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div className="flex items-center gap-2 p-2.5 rounded-lg bg-warning/10 border border-warning/30 text-xs" data-testid="expense-account-unmapped">
                        <div className="w-1 h-10 bg-warning rounded-full flex-shrink-0" />
                        <div className="flex-1">
                          <span className="font-semibold text-warning">Unmapped category</span>
                          <span className="text-muted-foreground ml-1.5">— posts to Miscellaneous Expenses.</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => openQuickMap(formData.category)}
                          className="px-2.5 py-1 rounded-md bg-primary text-white text-[11px] font-medium hover:bg-primary/90 transition-colors flex-shrink-0"
                          data-testid="expense-quick-map-btn"
                        >
                          <LinkIcon size={11} className="inline mr-1 -mt-0.5" />Map now
                        </button>
                      </div>
                    );
                  })()}
                  {/* iter540 — Pending-from-Bank banner. When the user
                      picks a fee category (Bank Fees / VAT on Bank Fees /
                      POS Fees / Government Fees), we surface unbooked
                      amounts already detected on bank statements and
                      offer one-click "Book all months" so they don't
                      have to type the SR figures manually. */}
                  {feeCatCode && (
                    <div className="rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-emerald-50 p-3.5 space-y-2.5" data-testid="fee-from-bank-banner">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <CalendarCheck2 size={16} className="text-sky-700 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-sky-900">
                              {feeBookingLoading ? 'Scanning bank statements…' : (
                                feeBookingData?.total_pending_months > 0
                                  ? `${feeBookingData.total_pending_months} month${feeBookingData.total_pending_months === 1 ? '' : 's'} pending · ${cur()} ${feeBookingData.total_pending_amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                                  : `${feeBookingData?.label || 'This category'} — all caught up`
                              )}
                            </div>
                            <div className="text-[11px] text-sky-700/80">
                              From your uploaded bank statements · skips months already booked
                            </div>
                          </div>
                        </div>
                        {feeBookingData && feeBookingData.total_pending_months > 0 && !feeBookingLoading && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={bookSelectedFeeMonths}
                            disabled={feeBookingPosting || Object.values(feeBookingPicked).filter(v => v).length === 0}
                            className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5 shrink-0"
                            data-testid="fee-book-selected-btn"
                          >
                            <Sparkles size={13} />
                            {feeBookingPosting ? 'Booking…' : `Book ${Object.values(feeBookingPicked).filter(v => v).length} month${Object.values(feeBookingPicked).filter(v => v).length === 1 ? '' : 's'}`}
                          </Button>
                        )}
                      </div>
                      {feeBookingData && (feeBookingData.months || []).length > 0 && !feeBookingLoading && (
                        <div className="flex flex-wrap gap-1.5 pt-1" data-testid="fee-month-chips">
                          {feeBookingData.months.map(m => {
                            const isPicked = !!feeBookingPicked[m.ym];
                            const disabled = m.booked || m.bookable === false;
                            const why = m.booked ? `Already booked on ${m.booked_at?.slice(0,10) || ''}` : m.bookable === false
                              ? (m.blocked_reason || (m.statement_complete === false ? 'Statement incomplete for this month — upload the full month first' : 'Nothing to book'))
                              : `${m.txns} txn${m.txns === 1 ? '' : 's'} across ${m.statement_ids?.length || 0} statement${(m.statement_ids?.length || 0) === 1 ? '' : 's'}`;
                            return (
                              <button
                                key={m.ym}
                                type="button"
                                onClick={() => { if (!disabled) setFeeBookingPicked({ ...feeBookingPicked, [m.ym]: !isPicked }); }}
                                disabled={disabled}
                                title={why}
                                data-testid={`fee-month-chip-${m.ym}`}
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-all ${
                                  disabled
                                    ? 'bg-muted/60 text-muted-foreground border-border cursor-not-allowed'
                                    : isPicked
                                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                                      : 'bg-white text-emerald-900 border-emerald-300 hover:bg-emerald-50'
                                }`}
                              >
                                {disabled && <CheckCircle2 size={11} />}
                                <span>{fmtMonthLabel(m.ym)}{m.bank_name ? ` · ${m.bank_name.split(' - ')[0]}` : ''}</span>
                                <span className={`font-mono ${disabled ? '' : isPicked ? 'text-white/90' : 'text-emerald-700'}`}>
                                  {cur()} {m.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {feeBookingData && (feeBookingData.months || []).length === 0 && !feeBookingLoading && (
                        <div className="text-[11px] text-sky-700 italic" data-testid="fee-no-bank-data">
                          No matching transactions found in uploaded bank statements yet.
                          You can still add this expense manually below.
                        </div>
                      )}
                      {/* iter541 — inline Undo for already-booked months */}
                      {feeBookingData && feeBookingData.total_booked_months > 0 && !feeBookingLoading && (
                        <div className="pt-2 border-t border-sky-200/60" data-testid="fee-already-booked-section">
                          <div className="text-[10px] uppercase tracking-wide font-semibold text-sky-700/80 mb-1.5">
                            Already booked · {feeBookingData.total_booked_months} month{feeBookingData.total_booked_months === 1 ? '' : 's'}
                          </div>
                          <FeeUndoList
                            category={feeCatCode}
                            months={feeBookingData.months.filter(m => m.booked)}
                            onUndo={(markerId) => undoAutoBooked(markerId, 'fee')}
                            onEdit={openAutoBookedEdit}
                            onNavigate={navigateToExpense}
                            fmtMonthLabel={fmtMonthLabel}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {/* iter541 — Employee-linked booking banner (Salary /
                      Iqama Renewal / Tickets / ID Card). Shows a TABLE
                      of bank txns with per-row employee picker, so each
                      books as its own expense linked to the right person. */}
                  {empCatCode && (
                    <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-3.5 space-y-2.5" data-testid="emp-from-bank-banner">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <CalendarCheck2 size={16} className="text-violet-700 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-violet-900">
                              {empBookingLoading ? 'Scanning bank statements…' : (
                                empBookingData?.total_pending_count > 0
                                  ? `${empBookingData.total_pending_count} pending payment${empBookingData.total_pending_count === 1 ? '' : 's'} · ${cur()} ${empBookingData.total_pending_amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                                  : `${empBookingData?.label || 'This category'} — no pending bank lines`
                              )}
                            </div>
                            <div className="text-[11px] text-violet-700/80">
                              Pick an employee for each row · books one expense per line
                            </div>
                          </div>
                        </div>
                        {empBookingData && empBookingData.total_pending_count > 0 && !empBookingLoading && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={bookSelectedEmpTxns}
                            disabled={empBookingPosting || Object.values(empRowState).filter(r => r.picked).length === 0}
                            className="h-8 bg-violet-600 hover:bg-violet-700 text-white text-xs gap-1.5 shrink-0"
                            data-testid="emp-book-selected-btn"
                          >
                            <Sparkles size={13} />
                            {empBookingPosting ? 'Booking…' : `Book ${Object.values(empRowState).filter(r => r.picked).length}`}
                          </Button>
                        )}
                      </div>
                      {empBookingData && (empBookingData.txns || []).filter(t => !t.booked).length > 0 && !empBookingLoading && (
                        <div className="rounded-lg border border-violet-200 bg-white overflow-hidden" data-testid="emp-txn-table">
                          <div className="max-h-72 overflow-y-auto">
                            <div className="overflow-x-auto">
                              <table className="w-full text-[11px]">
                                <thead className="bg-violet-100/60 sticky top-0">
                                  <tr className="text-left text-violet-900">
                                    <th className="p-1.5 w-7">
                                      <input
                                        type="checkbox"
                                        data-testid="emp-pick-all"
                                        checked={(() => {
                                          const pending = (empBookingData.txns || []).filter(t => !t.booked);
                                          return pending.length > 0 && pending.every(t => empRowState[t.txn_id]?.picked);
                                        })()}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          const next = { ...empRowState };
                                          for (const t of (empBookingData.txns || [])) {
                                            if (t.booked) continue;
                                            next[t.txn_id] = { ...(next[t.txn_id] || {}), picked: checked, employee_id: next[t.txn_id]?.employee_id ?? (t.suggested_employee_id || '') };
                                          }
                                          setEmpRowState(next);
                                        }}
                                      />
                                    </th>
                                    <th className="p-1.5">Date</th>
                                    <th className="p-1.5">Description</th>
                                    <th className="p-1.5 text-right">Amount</th>
                                    <th className="p-1.5">Employee *</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(empBookingData.txns || []).filter(t => !t.booked).map(t => {
                                    const rs = empRowState[t.txn_id] || { picked: false, employee_id: '' };
                                    return (
                                      <tr key={t.txn_id} className="border-t border-violet-100 hover:bg-violet-50/40" data-testid={`emp-row-${t.txn_id}`}>
                                        <td className="p-1.5">
                                          <input
                                            type="checkbox"
                                            checked={!!rs.picked}
                                            onChange={(e) => setEmpRowState({ ...empRowState, [t.txn_id]: { ...rs, picked: e.target.checked } })}
                                            data-testid={`emp-row-pick-${t.txn_id}`}
                                          />
                                        </td>
                                        <td className="p-1.5 whitespace-nowrap font-mono text-[10px]">{(t.date || '').slice(0, 10)}</td>
                                        <td className="p-1.5 max-w-[260px] truncate" title={t.description}>{t.description}</td>
                                        <td className="p-1.5 text-right font-mono font-bold text-violet-900">{cur()} {t.debit.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                        <td className="p-1.5">
                                          <select
                                            value={rs.employee_id || ''}
                                            onChange={(e) => setEmpRowState({ ...empRowState, [t.txn_id]: { ...rs, employee_id: e.target.value } })}
                                            className="h-7 w-full max-w-[180px] rounded border border-violet-300 bg-white px-1 text-[11px] focus:border-violet-500 focus:outline-none"
                                            data-testid={`emp-row-employee-${t.txn_id}`}
                                          >
                                            <option value="">— Select employee —</option>
                                            {(employees || []).map(emp => (
                                              <option key={emp.id} value={emp.id}>{emp.name}</option>
                                            ))}
                                          </select>
                                          {t.suggested_employee_name && rs.employee_id === t.suggested_employee_id && (
                                            <div className="text-[9px] text-emerald-600 mt-0.5">↑ matched from description</div>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                      {empBookingData && (empBookingData.txns || []).filter(t => !t.booked).length === 0 && !empBookingLoading && (
                        <div className="text-[11px] text-violet-700 italic" data-testid="emp-no-bank-data">
                          {empBookingData?.total_booked_count > 0
                            ? 'All matching bank lines are already booked — add a manual entry below if needed.'
                            : 'No matching transactions found yet. You can still add this expense manually below.'}
                        </div>
                      )}
                      {/* iter541 — already-booked section with Undo per row */}
                      {empBookingData && empBookingData.total_booked_count > 0 && !empBookingLoading && (
                        <div className="pt-2 border-t border-violet-200/60" data-testid="emp-already-booked-section">
                          <div className="text-[10px] uppercase tracking-wide font-semibold text-violet-700/80 mb-1.5">
                            Already booked · {empBookingData.total_booked_count} entr{empBookingData.total_booked_count === 1 ? 'y' : 'ies'}
                          </div>
                          <EmpUndoList
                            category={empCatCode}
                            rows={(empBookingData.txns || []).filter(t => t.booked)}
                            onUndo={(markerId) => undoAutoBooked(markerId, 'employee')}
                            onEdit={openAutoBookedEdit}
                            onNavigate={navigateToExpense}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {/* Advance Payment (pass-through) entry type */}
                  <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-2.5 space-y-2" data-testid="advance-payment-block">
                    <label className="flex items-center gap-2 text-xs font-semibold text-violet-800 cursor-pointer">
                      <input type="checkbox" className="h-3.5 w-3.5 accent-violet-600" checked={formData.is_advance}
                        data-testid="advance-payment-toggle"
                        onChange={async (e) => {
                          const on = e.target.checked;
                          setFormData({ ...formData, is_advance: on });
                          if (on && partners.length === 0) {
                            try { const r = await api.get('/partners'); setPartners(r.data?.partners || r.data || []); } catch { /* ignore */ }
                          }
                        }} />
                      Advance Payment (pass-through — link to an Expense or Purchase later)
                    </label>
                    {formData.is_advance && (
                      <div className="grid grid-cols-2 gap-2">
                        <select className="h-9 rounded-md border border-border bg-white text-xs px-2" value={formData.advance_person_type}
                          data-testid="advance-person-type"
                          onChange={(e) => setFormData({ ...formData, advance_person_type: e.target.value, advance_person_id: '' })}>
                          <option value="employee">Employee</option>
                          <option value="partner">Partner</option>
                        </select>
                        <select className="h-9 rounded-md border border-border bg-white text-xs px-2" value={formData.advance_person_id}
                          data-testid="advance-person-select"
                          onChange={(e) => setFormData({ ...formData, advance_person_id: e.target.value })}>
                          <option value="">Select {formData.advance_person_type}...</option>
                          {(formData.advance_person_type === 'employee' ? employees : partners).map((p) => (
                            <option key={p.id} value={p.id}>{p.name || p.full_name || p.employee_name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  {formData.category && getSubCats(formData.category).length > 0 && (
                    <div>
                      <Label>Sub-Category</Label>
                      <div className="flex gap-2 flex-wrap mt-2">
                        <button type="button" onClick={() => setFormData({...formData, sub_category: ''})}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${!formData.sub_category ? 'bg-primary text-white' : 'bg-muted/50 border-border hover:bg-muted'}`}>General</button>
                        {getSubCats(formData.category).map(s => (
                          <button key={s} type="button" onClick={() => setFormData({...formData, sub_category: s})}
                            className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${formData.sub_category === s ? 'bg-primary text-white' : 'bg-muted/50 border-border hover:bg-muted'}`}>{s}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {liveDup && (liveDup.count > 0 || liveDup.cross_category_count > 0) && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2" data-testid="expense-live-dup-warning">
                      <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
                      <div className="text-xs text-amber-800">
                        {liveDup.count > 0
                          ? <>A <b>same-category</b> expense of this amount at this branch already exists on {formData.date}. Make sure this isn't a duplicate.</>
                          : <>A same-day expense of <b>this amount at this branch</b> already exists{liveDup.cross_category_existing?.[0]?.category ? <> under <b>{liveDup.cross_category_existing[0].category}</b></> : null} on {formData.date}. Make sure this isn't a duplicate.</>}
                        {([...(liveDup.existing || []), ...(liveDup.cross_category_existing || [])].length > 0) && (
                          <span className="block text-[11px] text-amber-700 mt-0.5">
                            Existing: {[...(liveDup.existing || []), ...(liveDup.cross_category_existing || [])].slice(0, 3).map(e => `${e.category || '—'}${e.payment_mode ? ' (' + e.payment_mode + ')' : ''}`).join(' · ')}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>{t('amount')} * <span className="text-[10px] text-muted-foreground">(gross invoice total, incl. any VAT)</span></Label>
                      <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="SR 0.00" className="h-10" required data-testid="expense-amount-input" />
                    </div>
                    <div>
                      <Label>{t('payment_mode')}</Label>
                      <Select value={formData.payment_mode} onValueChange={(v) => {
                        setFormData({ ...formData, payment_mode: v });
                        if (v !== 'bank') setPickedBankTxn(null);
                      }}>
                        <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="cash">{t('pos_cash')}</SelectItem><SelectItem value="bank">{t('pos_bank')}</SelectItem><SelectItem value="credit">{t('pos_credit')}</SelectItem></SelectContent>
                      </Select>
                      {formData.payment_mode === 'cash' && (
                        <DrawerSourceSelect branchId={formData.branch_id} value={formData.drawer_shift_id}
                          onChange={(v) => setFormData({ ...formData, drawer_shift_id: v })} />
                      )}
                      {/* iter442e — Bank-statement picker.  Only relevant
                          when paying by bank — gives the user a "pick a
                          real bank line" shortcut so the expense links
                          to the bank statement automatically. */}
                      {formData.payment_mode === 'bank' && (
                        <div className="mt-1.5">
                          {pickedBankTxn ? (
                            <div className="rounded-md border border-info/30 bg-info/5 px-2 py-1.5 text-[11px] flex items-center justify-between gap-2"
                              data-testid="expense-picked-bank-txn">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium truncate">{pickedBankTxn.description}</div>
                                <div className="text-muted-foreground text-[10px]">{pickedBankTxn.date} · {pickedBankTxn.bank_name || 'Bank'} · {cur()} {pickedBankTxn.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                              </div>
                              <button type="button"
                                onClick={() => setPickedBankTxn(null)}
                                className="text-error hover:underline text-[10px]"
                                data-testid="expense-clear-bank-txn">
                                Clear
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3">
                              <button type="button"
                                onClick={() => { setBulkPickMode(false); setShowPickBank(true); }}
                                className="text-[11px] text-info hover:underline inline-flex items-center gap-1"
                                data-testid="expense-pick-bank-btn">
                                <Banknote size={11} /> Pick from bank statement…
                              </button>
                              <span className="text-[10px] text-muted-foreground">or</span>
                              <button type="button"
                                onClick={() => { setBulkPickMode(true); setShowPickBank(true); }}
                                className="text-[11px] text-violet-700 hover:underline inline-flex items-center gap-1"
                                data-testid="expense-pick-bank-bulk-btn"
                                title="Group similar bank lines and add many in one go">
                                <Banknote size={11} /> Bulk pick (group &amp; multi)…
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>{employeeLinkedCats.includes(formData.category) ? 'Employee' : 'Supplier'}</Label>
                      {employeeLinkedCats.includes(formData.category) ? (
                        <SearchableSelect
                          items={[{id: '', name: '- No Employee -'}, ...employees.map(e => ({id: e.id, name: e.name}))]}
                          value={formData.employee_id || ''}
                          onChange={(v) => setFormData({ ...formData, employee_id: v, supplier_id: '' })}
                          placeholder="Search employee..."
                          data-testid="expense-employee-select"
                        />
                      ) : (
                        <SearchableSelect
                          items={[{id: '', name: '- No Supplier -'}, ...suppliers]}
                          value={formData.supplier_id || ''}
                          onChange={(v) => setFormData({ ...formData, supplier_id: v, employee_id: '' })}
                          placeholder="Search supplier..."
                        />
                      )}
                    </div>
                    <div>
                      <Label>Paid By (Branch)</Label>
                      <Select value={formData.branch_id || "none"} onValueChange={(v) => setFormData({ ...formData, branch_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="h-10" data-testid="expense-paid-from-branch"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="none">-</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                      {(formData.expense_for_branch_id || formData.branch_id) && (
                        <div className="mt-1.5">
                          <BranchTaxBadge branchId={formData.expense_for_branch_id || formData.branch_id} compact />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* iter526 — Inline Fine attribution (auto-shown when category contains "fine" / "Saher" / "MOI" / "traffic") */}
                  {isFineCategory && (
                    <div className="p-4 rounded-xl border border-rose-200 dark:border-rose-900 bg-rose-50/60 dark:bg-rose-950/30 space-y-3" data-testid="expense-fine-block">
                      <div className="flex items-center gap-2 text-rose-700 dark:text-rose-300 text-sm font-medium">
                        <AlertTriangle size={14} /> Fine details — these will create a linked Fine record automatically
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Department / Authority</Label>
                          <Input value={formData.fine_department} onChange={(e) => setFormData({ ...formData, fine_department: e.target.value })} placeholder="e.g. Saher, MOI, Traffic Dept" data-testid="expense-fine-department" />
                        </div>
                        <div>
                          <Label className="text-xs">Vehicle</Label>
                          <Select
                            value={formData.fine_vehicle_id || 'none'}
                            onValueChange={(v) => {
                              if (v === 'none') { setFormData({ ...formData, fine_vehicle_id: '', fine_plate_number: '' }); return; }
                              const veh = fleetVehicles.find(x => x.id === v) || {};
                              setFormData({ ...formData, fine_vehicle_id: v, fine_plate_number: veh.plate_number || veh.vehicle_number || veh.name || '' });
                            }}
                          >
                            <SelectTrigger data-testid="expense-fine-vehicle"><SelectValue placeholder="No vehicle" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No vehicle</SelectItem>
                              {fleetVehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.plate_number || v.vehicle_number || v.name || v.id.slice(0,6)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Plate Number</Label>
                          <Input value={formData.fine_plate_number} onChange={(e) => setFormData({ ...formData, fine_plate_number: e.target.value })} placeholder="e.g. ABC 1234" data-testid="expense-fine-plate" />
                        </div>
                        <div>
                          <Label className="text-xs">Violation Date</Label>
                          <Input type="date" value={formData.fine_violation_date} onChange={(e) => setFormData({ ...formData, fine_violation_date: e.target.value })} data-testid="expense-fine-violation-date" />
                        </div>
                        <div className="sm:col-span-2 flex items-center gap-3">
                          <label className="flex items-center gap-2 cursor-pointer text-sm">
                            <input type="checkbox" checked={formData.fine_deduct_from_salary} onChange={e => setFormData({ ...formData, fine_deduct_from_salary: e.target.checked })} className="rounded" data-testid="expense-fine-deduct-toggle" />
                            <span>Deduct from employee salary</span>
                          </label>
                          {formData.fine_deduct_from_salary && (
                            <Input
                              type="number" step="0.01"
                              value={formData.fine_monthly_deduction}
                              onChange={e => setFormData({ ...formData, fine_monthly_deduction: e.target.value })}
                              placeholder="Monthly deduction"
                              className="h-9 w-44"
                              data-testid="expense-fine-monthly"
                            />
                          )}
                        </div>
                      </div>
                      {!formData.employee_id && (
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                          Tip: pick an Employee above to attribute this fine and (optionally) auto-deduct from payroll.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* VAT treatment — chosen per supplier invoice; amount is gross, VAT separated (never added on top) */}
                  <VatTreatmentFields value={{ vat_treatment: formData.vat_treatment, supplier_vat_number: formData.supplier_vat_number, invoice_number: formData.invoice_number }}
                    onChange={(p) => setFormData((f) => ({ ...f, ...p }))} amount={formData.amount} rate={taxConfig?.vat_rate || 15} withInvoice={false} testPrefix="expense-vat" />

                  {/* Credit warning for supplier */}
                  {formData.supplier_id && formData.payment_mode === 'credit' && (
                    <div className="p-3 bg-warning/10 dark:bg-amber-900/20 border border-warning/30 dark:border-amber-700 rounded-lg flex items-start gap-2">
                      <AlertTriangle size={16} className="text-warning mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-warning dark:text-amber-300">Credit Purchase</p>
                        <p className="text-warning dark:text-amber-400 text-xs">
                          This expense will be added to supplier's credit balance. Pay later via Supplier Payments.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className={formData.expense_for_branch_id ? 'p-2 -m-2 rounded-lg bg-info/5 border border-info/30' : ''}>
                      <Label className="flex items-center gap-1">
                        Expense For (Branch)
                        {formData.expense_for_branch_id && <span className="text-[9px] text-info font-bold uppercase bg-info/10 px-1.5 py-0.5 rounded">Cross-Branch</span>}
                      </Label>
                      <Select value={formData.expense_for_branch_id || "none"} onValueChange={(v) => setFormData({ ...formData, expense_for_branch_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="h-10" data-testid="expense-for-branch"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="none">- Same as Branch (default) -</SelectItem>{branches.filter(b => b.id !== formData.branch_id).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {formData.expense_for_branch_id
                          ? `${branches.find(b => b.id === formData.branch_id)?.name || 'This branch'} pays; ${branches.find(b => b.id === formData.expense_for_branch_id)?.name || ''} owes. Shows on Inter-Branch Ledger.`
                          : 'Set if this expense was paid on behalf of another branch'}
                      </p>
                    </div>
                    <div>
                      <Label>{t('date')}</Label>
                      <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="h-10" />
                    </div>
                    <div>
                      <Label>{t('description')}</Label>
                      <div className="relative">
                        <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder={t('description')} className="h-10 pr-16"
                          onBlur={async () => {
                            if (AI_CATEGORY_SUGGEST && formData.description && formData.description.length > 3 && !formData.category) {
                              try {
                                const { data } = await api.post('/expenses/auto-categorize', { description: formData.description });
                                if (data.category && data.category !== 'general') {
                                  const matched = mainCats.find(c => c.toLowerCase() === data.category.toLowerCase());
                                  if (matched) { setFormData(f => ({ ...f, category: matched })); toast.info(`AI suggested: ${matched}`); }
                                }
                              } catch {}
                            }
                          }}
                        />
                        {AI_CATEGORY_SUGGEST && !formData.category && formData.description?.length > 3 && (
                          <span className="absolute right-2 top-2.5 text-[9px] text-purple-500 bg-purple-50 px-1.5 py-0.5 rounded" data-testid="ai-cat-hint">AI</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label>Invoice No. {formData.vat_treatment === 'standard' ? '*' : '(Optional)'}</Label>
                      <Input value={formData.invoice_number} onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })} placeholder="e.g. INV-2024-001" className="h-10" data-testid="expense-invoice-number" />
                    </div>
                    <div>
                      <Label>Invoice (Optional)</Label>
                      <Input type="file" accept="image/*,.pdf" className="h-10 text-xs" data-testid="expense-invoice-upload"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          try {
                            const fd = new FormData();
                            fd.append('file', file);
                            const res = await api.post('/expenses/upload-bill', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            setFormData(f => ({ ...f, bill_image_url: res.data.bill_url }));
                            toast.success('Invoice uploaded');
                          } catch { toast.error('Upload failed'); }
                        }}
                      />
                      {formData.bill_image_url && <span className="text-[10px] text-success mt-0.5 block">Attached</span>}
                    </div>
                    <div className="flex items-end">
                      <Button type="submit" className="rounded-xl w-full h-10" data-testid="add-expense-btn-2"><Plus size={16} className="mr-2" />{t('add')}</Button>
                    </div>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>

          {/* ALL EXPENSES */}
          <TabsContent value="list">
            <Card className="border-border">
              <CardHeader><CardTitle className="font-outfit text-base flex justify-between"><span className="flex items-center gap-2">{t('all_expenses')} <CheckExportBtn disabled={!expenses.length} testId="check-export-expenses-page" title="Validate expense rows, then export CSV" onClick={() => handleExport(expenses.map((e) => ({ counterparty: e.supplier_name || e.category || '', date: e.date, amount: e.amount, status: e.description || '' })), 'expenses_report.csv', REPORT_EXPORT_COLS, 'expense records')} /></span> <span className="text-error inline-flex items-center gap-1">{t('total_label')}: <RiyalIcon size="0.7em" /> {totalExp.toFixed(2)}</span></CardTitle></CardHeader>
              <CardContent>
                {/* Bulk VAT Toolbar */}
                <div className="mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant={bulkVatMode ? "default" : "outline"} onClick={() => { setBulkVatMode(!bulkVatMode); setSelectedIds(new Set()); }} className="h-8 text-xs rounded-full" data-testid="bulk-vat-toggle">
                      {bulkVatMode ? 'Cancel Bulk' : 'Bulk VAT Update'}
                    </Button>
                    {bulkVatMode && (
                      <>
                        <Button size="sm" variant="outline" onClick={selectAllVisible} className="h-8 text-xs rounded-full">{selectedIds.size === expenses.length ? 'Deselect All' : 'Select All'}</Button>
                        <span className="text-xs text-muted-foreground">{selectedIds.size} selected</span>
                        <div className="h-5 w-px bg-muted" />
                        <Input type="number" value={bulkVatRate} onChange={e => setBulkVatRate(parseFloat(e.target.value) || 0)} className="w-16 h-8 text-xs" placeholder="%" />
                        <span className="text-xs text-muted-foreground">%</span>
                        <Select value={bulkVatInclusive ? 'inclusive' : 'exclusive'} onValueChange={v => setBulkVatInclusive(v === 'inclusive')}>
                          <SelectTrigger className="h-8 w-[100px] text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="inclusive">Inclusive</SelectItem><SelectItem value="exclusive">Exclusive</SelectItem></SelectContent>
                        </Select>
                        <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
                          <input type="checkbox" checked={bulkSkipExisting} onChange={e => setBulkSkipExisting(e.target.checked)} className="w-3.5 h-3.5 rounded" />
                          Skip if VAT exists
                        </label>
                        <Button size="sm" onClick={applyBulkVat} disabled={selectedIds.size === 0 && !bulkFilterSupplier && !bulkFilterStartDate} className="h-8 text-xs rounded-full bg-teal-600 hover:bg-teal-700" data-testid="apply-bulk-vat">Apply VAT</Button>
                      </>
                    )}
                  </div>
                  {bulkVatMode && (
                    <div className="flex items-center gap-2 mt-2 pl-2 flex-wrap">
                      <span className="text-[10px] font-semibold text-muted-foreground uppercase">Or filter by:</span>
                      <Select value={bulkFilterSupplier || "none"} onValueChange={v => setBulkFilterSupplier(v === "none" ? "" : v)}>
                        <SelectTrigger className="h-7 w-[140px] text-[11px]"><SelectValue placeholder="Supplier" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">All Suppliers</SelectItem>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="date" value={bulkFilterStartDate} onChange={e => setBulkFilterStartDate(e.target.value)} className="h-7 w-[120px] text-[11px]" placeholder="From" />
                      <Input type="date" value={bulkFilterEndDate} onChange={e => setBulkFilterEndDate(e.target.value)} className="h-7 w-[120px] text-[11px]" placeholder="To" />
                      {(bulkFilterSupplier || bulkFilterStartDate) && (
                        <Button size="sm" onClick={applyBulkVat} className="h-7 text-[11px] rounded-full bg-teal-600 hover:bg-teal-700">Apply to Filtered</Button>
                      )}
                    </div>
                  )}
                </div>

                <ExpenseSummaryCards ct={serverTotals.classified_totals} />
                <PeriodAccountingPanel start={searchFilters?.date?.start || dateRange?.start} end={searchFilters?.date?.end || dateRange?.end} branchId={activeBackendFilters.branch_id} branchIds={branchSel} />
                {/* Grand Total Summary */}
                {filtered.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 sticky bottom-0 z-20 bg-background/95 backdrop-blur-sm py-2 -mx-1 px-1 rounded-lg border-t border-border/40 shadow-[0_-2px_8px_-2px_rgba(0,0,0,0.06)]" data-testid="expenses-summary-bar">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-red-50 to-red-100 border border-error/30 card-enter">
                      <div className="flex items-center justify-between gap-1"><div className="text-[10px] text-error uppercase tracking-wider">All recorded rows ({inclVat ? 'Incl. VAT' : 'Excl. VAT'}) · incl. supplier payments</div><VatModeToggle /></div>
                      <div className="text-lg font-bold text-error inline-flex items-center gap-1" data-testid="expenses-grand-total"><RiyalIcon size="0.7em" /> {(inclVat ? totalExp : totalExp - (serverTotals.vat_total || 0)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                      <div className="text-[10px] text-muted-foreground">{filtered.length} entries · input VAT {(serverTotals.vat_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} · payments incl. VAT</div>
                    </div>
                    <div className="p-3 rounded-xl bg-success/10 border border-success/30 card-enter">
                      <div className="text-[10px] text-success uppercase tracking-wider">Cash</div>
                      <div className="text-lg font-bold text-success inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(serverTotals.cash_total || filtered.filter(e => e.payment_mode === 'cash').reduce((s, e) => s + (e.amount||0), 0)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-info/10 border border-info/30 card-enter">
                      <div className="text-[10px] text-info uppercase tracking-wider">Bank</div>
                      <div className="text-lg font-bold text-info inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(serverTotals.bank_total || filtered.filter(e => e.payment_mode === 'bank').reduce((s, e) => s + (e.amount||0), 0)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-warning/10 border border-warning/30 card-enter">
                      <div className="text-[10px] text-warning uppercase tracking-wider">Credit</div>
                      <div className="text-lg font-bold text-warning inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(serverTotals.credit_total || filtered.filter(e => e.payment_mode === 'credit').reduce((s, e) => s + (e.amount||0), 0)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                    </div>
                  </div>
                )}
                {/* Mobile card view */}
                <div className="sm:hidden space-y-2">
                  {filtered.map(e => (
                    <div key={e.id} className="p-3 border rounded-xl bg-white space-y-2" data-testid={`expense-card-${e.id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <Badge variant="secondary" className="capitalize text-xs">{e.category?.replace('_',' ')}</Badge>
                          {e.sub_category && <Badge variant="outline" className="ml-1 text-[10px] capitalize">{e.sub_category}</Badge>}
                        </div>
                        <span className="text-base font-bold text-error inline-flex items-center gap-0.5"><RiyalIcon size="0.7em" /> {e.amount.toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{e.description || t('no_data')}</p>
                      <div className="flex justify-between items-center text-[10px] text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <span>{safeFormat(e.date, 'MMM dd, yyyy')}</span>
                          {e.created_by_name && <span className="text-muted-foreground">by {e.created_by_name}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`capitalize text-[10px] ${e.payment_mode === 'cash' ? 'bg-cash/20 text-cash' : e.payment_mode === 'bank' ? 'bg-bank/20 text-bank' : 'bg-credit/20 text-credit'}`}>{e.payment_mode}</Badge>
                          <span>{branches.find(b => b.id === e.branch_id)?.name || '-'}</span>
                          <Button size="sm" variant="ghost" data-testid={`delete-expense-mobile-${e.id}`} onClick={async () => { const _p = askCancel('Delete this expense?', !!e.journal_entry_id); if (_p) { try { const _r = await api.delete(`/expenses/${e.id}`, { params: _p }); toast.success(_r.data?.message || 'Expense deleted'); fetchData(); } catch(err) { toast.error(err.response?.data?.detail || 'Failed to delete expense'); } }}} className="h-6 w-6 p-0 text-error"><Trash2 size={12} /></Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {filtered.length === 0 && <FilterEmptyNudge show onReset={() => { setDateRange({ start: localToday(), end: localToday() }); setBranchSel([]); setActiveBackendFilters({}); }} label={t('no_data') || 'No expenses for the current filter'} />}
                </div>
                {/* Desktop: default flat transaction list, optional daily grouping */}
                <div className="hidden sm:block">
                  {!dailyView && (
                    filtered.length === 0
                      ? <FilterEmptyNudge show onReset={() => { setDateRange({ start: localToday(), end: localToday() }); setBranchSel([]); setActiveBackendFilters({}); }} label={t('no_data') || 'No expenses for the current filter'} />
                      : <ExpensesFlatTable expenses={filtered} branches={branches} safeFormat={safeFormat} onDelete={(id) => { const _p = askCancel('Delete this expense?', !!filtered.find((x) => x.id === id)?.journal_entry_id); if (_p) { api.delete(`/expenses/${id}`, { params: _p }).then((_r) => { toast.success(_r.data?.message || 'Expense deleted'); fetchData(); }).catch((err) => toast.error(err.response?.data?.detail || 'Failed to delete expense')); } }} />
                  )}
                  {dailyView && totalRecords > expenses.length && (
                    <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="expenses-view-truncated">
                      This daily view groups the first {expenses.length.toLocaleString()} of {totalRecords.toLocaleString()} expenses. Totals above are complete; narrow the date range for a complete grouping.
                    </div>
                  )}
                  {dailyView && (() => {
                    // Group expenses by date
                    const grouped = {};
                    filtered.forEach(exp => {
                      const dateKey = exp.date ? safeFormat(exp.date, 'yyyy-MM-dd', 'unknown') : 'unknown';
                      if (!grouped[dateKey]) {
                        grouped[dateKey] = { date: exp.date, dateKey, cash: 0, bank: 0, credit: 0, total: 0, categories: {}, branches: {}, items: [], count: 0 };
                      }
                      const g = grouped[dateKey];
                      g.count++;
                      g.total += exp.amount || 0;
                      if (exp.payment_mode === 'cash') g.cash += exp.amount || 0;
                      else if (exp.payment_mode === 'bank') g.bank += exp.amount || 0;
                      else g.credit += exp.amount || 0;
                      const cat = exp.supplier_name || exp.paid_to || exp.category || 'Other';
                      g.categories[cat] = (g.categories[cat] || 0) + (exp.amount || 0);
                      const bName = branches.find(b => b.id === exp.branch_id)?.name || 'Other';
                      g.branches[bName] = (g.branches[bName] || 0) + (exp.amount || 0);
                      g.items.push(exp);
                    });
                    const dailyData = Object.values(grouped).sort((a, b) => safeNewDate(b.date) - safeNewDate(a.date));

                    // Detect duplicate entries per day (same branch + same supplier/employee + same category + same amount).
                    // iter432 — Tightened to mirror the create-time dedupe scope
                    // (employee_id + supplier_id + category) so two different
                    // employees getting the same salary or two different
                    // suppliers paid the same amount on the same day no
                    // longer get falsely flagged.
                    dailyData.forEach(day => {
                      const dupeKeys = {};
                      day.items.forEach(exp => {
                        const amt = (exp.amount || 0).toFixed(2);
                        if (parseFloat(amt) <= 0) return;
                        const key = [
                          exp.branch_id || 'none',
                          exp.supplier_id || 'nosup',
                          exp.employee_id || 'noemp',
                          (exp.category || 'nocat').toLowerCase(),
                          amt,
                        ].join('_');
                        if (!dupeKeys[key]) dupeKeys[key] = [];
                        dupeKeys[key].push(exp.id);
                      });
                      const dupeIds = new Set();
                      Object.values(dupeKeys).forEach(ids => {
                        if (ids.length > 1) ids.forEach(id => dupeIds.add(id));
                      });
                      day.duplicateIds = dupeIds;
                      day.hasDuplicates = dupeIds.size > 0;
                    });

                    if (dailyData.length === 0) return <div className="text-center py-8 text-muted-foreground">{t('no_data')}</div>;

                    return (
                      <div>
                        {/* Desktop Table */}
                        <div className="border rounded-lg overflow-hidden hidden md:block">
                        {/* Header */}
                        <div className="bg-muted/50 dark:bg-muted">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm table-fixed">
                              <thead><tr>
                                <th className="px-3 py-3 text-left font-medium text-muted-foreground w-[4%]"></th>
                                <th className="px-3 py-3 text-left font-medium text-muted-foreground w-[14%]">Date</th>
                                <th className="px-3 py-3 text-right font-medium text-muted-foreground w-[12%]">Total</th>
                                <th className="px-3 py-3 text-right font-medium text-muted-foreground w-[12%]">Cash</th>
                                <th className="px-3 py-3 text-right font-medium text-muted-foreground w-[12%]">Bank</th>
                                <th className="px-3 py-3 text-right font-medium text-muted-foreground w-[12%]">Credit</th>
                                <th className="px-3 py-3 text-left font-medium text-muted-foreground w-[34%]">Categories</th>
                              </tr></thead>
                            </table>
                          </div>
                        </div>
                        {/* Body */}
                        <div className="max-h-[600px] overflow-y-auto">
                          {dailyData.map(day => (
                            <div key={day.dateKey}>
                              {/* Day Summary Row */}
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm table-fixed">
                                  <tbody>
                                    <tr className={`border-b hover:bg-muted/50 cursor-pointer transition-colors ${day.hasDuplicates ? 'bg-orange-50/60' : ''}`}
                                      onClick={() => setExpandedDates(prev => ({ ...prev, [day.dateKey]: !prev[day.dateKey] }))}
                                      data-testid={`expense-day-row-${day.dateKey}`}>
                                      <td className="px-3 py-3 w-[4%]">
                                        {expandedDates[day.dateKey] ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevronRight size={14} className="text-muted-foreground" />}
                                      </td>
                                      <td className="px-3 py-3 w-[14%]">
                                        <div className="font-semibold text-sm flex items-center gap-1.5">
                                          {safeFormat(day.date, 'MMM dd, yyyy')}
                                          {day.hasDuplicates && (
                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-bold border border-orange-300" data-testid={`exp-duplicate-warning-${day.dateKey}`}>
                                              <AlertTriangle size={10} /> Duplicate
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground">{day.count} entries</div>
                                      </td>
                                      <td className="px-3 py-3 text-right w-[12%]"><span className="text-sm font-bold text-error inline-flex items-center gap-0.5"><RiyalIcon size="0.7em" /> {day.total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></td>
                                      <td className="px-3 py-3 text-right w-[12%]">{day.cash > 0 ? <span className="inline-flex items-center gap-0.5 px-2 py-1 rounded bg-success/10 text-success text-xs font-semibold border border-success/30"><RiyalIcon size="0.85em" /> {day.cash.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span> : <span className="text-xs text-muted-foreground">-</span>}</td>
                                      <td className="px-3 py-3 text-right w-[12%]">{day.bank > 0 ? <span className="inline-flex items-center gap-0.5 px-2 py-1 rounded bg-info/10 text-info text-xs font-semibold border border-info/30"><RiyalIcon size="0.85em" /> {day.bank.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span> : <span className="text-xs text-muted-foreground">-</span>}</td>
                                      <td className="px-3 py-3 text-right w-[12%]">{day.credit > 0 ? <span className="inline-flex items-center gap-0.5 px-2 py-1 rounded bg-warning/10 text-warning text-xs font-semibold border border-warning/30"><RiyalIcon size="0.85em" /> {day.credit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span> : <span className="text-xs text-muted-foreground">-</span>}</td>
                                      <td className="px-3 py-3 w-[34%]">
                                        <div className="flex gap-1 flex-wrap">
                                          {Object.entries(day.categories).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([cat, amt]) => (
                                            <span key={cat} className="text-[10px] px-1.5 py-0.5 bg-muted text-foreground rounded border border-border">{cat}: {amt.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                                          ))}
                                          {Object.keys(day.categories).length > 4 && <span className="text-[10px] text-muted-foreground">+{Object.keys(day.categories).length - 4} more</span>}
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              {/* Expanded Individual Entries */}
                              {expandedDates[day.dateKey] && (
                                <div className="bg-muted/50 border-b">
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <tbody>
                                        {day.items.map(exp => (
                                          <tr key={exp.id} className={`border-b border-border hover:bg-white/50 ${day.duplicateIds.has(exp.id) ? 'bg-orange-50/80 border-l-4 border-l-orange-400' : ''}`} data-expense-id={exp.id} data-testid={`expense-detail-${exp.id}`}>
                                            <td className="px-3 py-2 pl-10 w-[18%]">
                                              <div className="flex items-center gap-1 flex-wrap">
                                                <Badge variant="secondary" className="capitalize text-[10px]">{exp.category?.replace('_',' ')}</Badge>
                                                {exp.sub_category && <Badge variant="outline" className="text-[10px] capitalize">{exp.sub_category}</Badge>}
                                                {/* iter521 — Source badge so users can spot bank-converted expenses */}
                                                {exp.source && (exp.source.startsWith('bank_statement_')) && (
                                                  <Badge className="text-[9px] bg-violet-100 text-violet-700 border border-violet-200" title={`Created from bank statement${exp.source_group_key ? ' · group: ' + exp.source_group_key : ''}`}>
                                                    📊 Bank
                                                  </Badge>
                                                )}
                                                {day.duplicateIds.has(exp.id) && (
                                                  <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-orange-100 text-orange-700 text-[9px] font-bold border border-orange-300" data-testid={`exp-duplicate-badge-${exp.id}`}>
                                                    <Copy size={8} /> Possible duplicate
                                                  </span>
                                                )}
                                                {String(exp.account_name || exp.account || exp.posted_account || '').toLowerCase().includes('miscellaneous') && (
                                                  <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-orange-100 text-orange-700 text-[9px] font-bold border border-orange-300" title="This expense fell back to the Miscellaneous account because its category is unmapped. Map it in Category → Account settings." data-testid={`exp-unmapped-badge-${exp.id}`}>
                                                    <AlertTriangle size={8} /> Unmapped
                                                  </span>
                                                )}
                                              </div>
                                              <div className="text-[9px] text-muted-foreground mt-0.5">{exp.date ? safeFormat(exp.date, 'hh:mm a', '') : ''} {exp.supplier_name ? `• ${exp.supplier_name}` : ''}</div>
                                            </td>
                                            <td className="px-3 py-2 text-sm w-[18%]" data-testid={`exp-desc-cell-${exp.id}`}>
                                              <InlineEditCell
                                                value={exp.description || ''}
                                                onSave={(v) => inlineUpdateExpense(exp.id, { description: v })}
                                                format={(v) => v || '-'}
                                                testid={`exp-desc-${exp.id}`}
                                                minWidth={120}
                                              />
                                            </td>
                                            <td className="px-3 py-2 text-sm truncate w-[8%]">{exp.invoice_number ? <span className="font-mono text-xs text-info bg-info/10 px-1.5 py-0.5 rounded">{exp.invoice_number}</span> : <span className="text-muted-foreground">-</span>}</td>
                                            <td className="px-3 py-2 text-sm truncate w-[10%]">{exp.supplier_id ? (suppliers.find(s => s.id === exp.supplier_id)?.name || '-') : '-'}</td>
                                            <td className="px-3 py-2 text-sm w-[10%]">
                                              {exp.owner_branch_name || branches.find(b => b.id === exp.branch_id)?.name || '-'}
                                              {exp.is_cross_branch && <span className="ml-1 inline-block rounded bg-info/10 px-1 py-0.5 text-[9px] font-semibold uppercase text-info" title={`Paid by ${exp.paid_by_branch_name} for ${exp.owner_branch_name}`} data-testid={`expense-paidby-daily-${exp.id}`}>Paid by {exp.paid_by_branch_name}</span>}
                                            </td>
                                            <td className="px-3 py-2 text-right w-[12%]" data-testid={`exp-amount-cell-${exp.id}`}>
                                              <span className="font-bold text-sm inline-flex items-center gap-0.5">
                                                <RiyalIcon size="0.7em" />
                                                <InlineEditCell
                                                  value={exp.amount || 0}
                                                  type="number"
                                                  step="0.01"
                                                  onSave={(v) => inlineUpdateExpense(exp.id, { amount: Number(v) })}
                                                  format={(v) => Number(v || 0).toFixed(2)}
                                                  testid={`exp-amount-${exp.id}`}
                                                  minWidth={70}
                                                />
                                              </span>
                                              {exp.vat_amount > 0 && <div className="text-[9px] text-info">VAT: {cur()} {exp.vat_amount?.toFixed(2)} ({exp.vat_rate}%)</div>}
                                            </td>
                                            <td className="px-3 py-2 w-[10%]">
                                              <Badge className={`capitalize text-[10px] ${exp.payment_mode === 'cash' ? 'bg-cash/20 text-cash' : exp.payment_mode === 'bank' ? 'bg-bank/20 text-bank' : 'bg-credit/20 text-credit'}`}>{exp.payment_mode}</Badge>
                                            </td>
                                            <td className="px-3 py-2 w-[10%]">
                                              {exp.created_by_name && <span className="text-[10px] text-muted-foreground" data-testid={`expense-created-by-${exp.id}`}>{exp.created_by_name}</span>}
                                            </td>
                                            <td className="px-3 py-2 text-right w-[6%]">
                                              <div className="flex items-center gap-0.5 justify-end">
                                                {bulkVatMode && <input type="checkbox" checked={selectedIds.has(exp.id)} onChange={() => toggleSelectExpense(exp.id)} className="w-3.5 h-3.5 rounded mr-1" onClick={e => e.stopPropagation()} />}
                                                <Button size="sm" variant="ghost" data-testid={`edit-expense-${exp.id}`} onClick={(e) => { e.stopPropagation(); openEditExpense(exp); }} className="h-7 text-info hover:text-info px-1" title="Edit"><Settings2 size={12} /></Button>
                                                <Button size="sm" variant="ghost" data-testid={`delete-expense-${exp.id}`} onClick={async (e) => { e.stopPropagation(); const _p = askCancel('Delete this expense?', !!exp.journal_entry_id); if (_p) { try { const _r = await api.delete(`/expenses/${exp.id}`, { params: _p }); toast.success(_r.data?.message || 'Expense deleted'); fetchData(); } catch(err) { toast.error(err.response?.data?.detail || 'Failed to delete expense'); } }}} className="h-7 text-error px-1"><Trash2 size={12} /></Button>
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {/* Footer */}
                        <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/50 border-t">
                          Showing {filtered.length.toLocaleString()} expenses across {dailyData.length} days
                        </div>
                      </div>
                        {/* Mobile Cards */}
                        <div className="md:hidden space-y-3">
                          {dailyData.map(day => (
                            <div key={day.dateKey} className="border rounded-xl overflow-hidden dark:border-stone-700" data-testid={`expense-mobile-card-${day.dateKey}`}>
                              <div className={`p-3 cursor-pointer active:bg-muted/50 ${day.hasDuplicates ? 'bg-orange-50/60' : ''}`}
                                onClick={() => setExpandedDates(prev => ({ ...prev, [day.dateKey]: !prev[day.dateKey] }))}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-sm font-outfit dark:text-white">{safeFormat(day.date, 'MMM dd, yyyy')}</span>
                                    {day.hasDuplicates && <AlertTriangle size={12} className="text-orange-500" />}
                                  </div>
                                  <span className="font-bold text-sm text-error inline-flex items-center gap-0.5"><RiyalIcon size="0.7em" /> {day.total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {day.cash > 0 && <span className="px-2 py-0.5 rounded-full bg-success/10 text-success text-[10px] font-semibold border border-success/30">Cash {day.cash.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>}
                                  {day.bank > 0 && <span className="px-2 py-0.5 rounded-full bg-info/10 text-info text-[10px] font-semibold border border-info/30">Bank {day.bank.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>}
                                  {day.credit > 0 && <span className="px-2 py-0.5 rounded-full bg-warning/10 text-warning text-[10px] font-semibold border border-warning/30">Credit {day.credit.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>}
                                </div>
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {Object.entries(day.categories).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([cat, amt]) => (
                                    <span key={cat} className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded">{cat}: {amt.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                                  ))}
                                  <span className="text-[10px] text-muted-foreground">{day.count} entries</span>
                                </div>
                              </div>
                              {expandedDates[day.dateKey] && (
                                <div className="border-t bg-muted/50 dark:bg-muted/50 divide-y divide-border dark:divide-stone-700">
                                  {day.items.map(exp => (
                                    <div key={exp.id} className="p-3" data-testid={`expense-mobile-detail-${exp.id}`}>
                                      <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <Badge variant="secondary" className="capitalize text-[10px]">{exp.category?.replace('_',' ')}</Badge>
                                          <Badge className={`capitalize text-[10px] ${exp.payment_mode === 'cash' ? 'bg-cash/20 text-cash' : exp.payment_mode === 'bank' ? 'bg-bank/20 text-bank' : 'bg-credit/20 text-credit'}`}>{exp.payment_mode}</Badge>
                                        </div>
                                        <span className="font-bold text-sm inline-flex items-center gap-0.5"><RiyalIcon size="0.7em" /> {exp.amount.toFixed(2)}</span>
                                      </div>
                                      {exp.description && <p className="text-xs text-muted-foreground line-clamp-1">{exp.description}</p>}
                                      <div className="flex items-center justify-between mt-1">
                                        <span className="text-[10px] text-muted-foreground">{exp.owner_branch_name || branches.find(b => b.id === exp.branch_id)?.name || ''}{exp.is_cross_branch ? ` (paid by ${exp.paid_by_branch_name})` : ''} {exp.supplier_id ? '| ' + (suppliers.find(s => s.id === exp.supplier_id)?.name || '') : ''}</span>
                                        <Button size="sm" variant="ghost" onClick={() => { const _p = askCancel('Delete?', !!exp.journal_entry_id); if (_p) { api.delete(`/expenses/${exp.id}`, { params: _p }).then((_r) => { toast.success(_r.data?.message || 'Deleted'); fetchData(); }); }}} className="h-6 px-1 text-error"><Trash2 size={12} /></Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                          <div className="text-xs text-muted-foreground text-center py-2">Showing {filtered.length.toLocaleString()} expenses across {dailyData.length} days</div>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Pagination Controls */}
                  {totalRecords > 0 && (
                    <div className="flex items-center justify-between mt-4 px-2 gap-2 flex-wrap" data-testid="expenses-pagination">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{totalRecords} total records</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">Page size</span>
                          <select
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                            className="h-8 rounded-md border border-border bg-background text-xs px-2"
                            data-testid="expenses-page-size"
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                          </select>
                        </div>
                      </div>
                      {totalPages > 1 && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" disabled={currentPage <= 1} className="h-8 w-8 p-0"
                          onClick={() => fetchData(currentPage - 1)} data-testid="expenses-prev-page"><ChevronLeft size={14} /></Button>
                        {(() => {
                          const pages = [];
                          const maxVisible = 5;
                          let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                          let end = Math.min(totalPages, start + maxVisible - 1);
                          if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
                          if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
                          for (let i = start; i <= end; i++) pages.push(i);
                          if (end < totalPages) { if (end < totalPages - 1) pages.push('...'); pages.push(totalPages); }
                          return pages.map((p, idx) => p === '...' ? (
                            <span key={`dots-${idx}`} className="text-xs text-muted-foreground px-1">...</span>
                          ) : (
                            <Button key={p} size="sm" variant={p === currentPage ? 'default' : 'outline'} className="h-8 w-8 p-0 text-xs"
                              onClick={() => fetchData(p)} data-testid={`expenses-page-${p}`}>{p}</Button>
                          ));
                        })()}
                        <Button size="sm" variant="outline" disabled={currentPage >= totalPages} className="h-8 w-8 p-0"
                          onClick={() => fetchData(currentPage + 1)} data-testid="expenses-next-page"><ChevronRight size={14} /></Button>
                      </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* RECURRING & PLANNED */}
          <TabsContent value="recurring">
            <div className="space-y-4">
              <Card className="border-border">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="font-outfit text-base">Recurring Expenses (Rent, Insurance, Renewals)</CardTitle>
                    <Button size="sm" className="rounded-xl" onClick={() => document.getElementById('rec-form').classList.toggle('hidden')}><Plus size={14} className="mr-1" />Add Recurring</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Branch Fixed Expense Summary */}
                  {branchSummary && branchSummary.grand_total?.items > 0 && (
                    <div className="mb-4 p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 border border-indigo-200/60 dark:border-indigo-900/40" data-testid="branch-fixed-summary">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold font-outfit text-foreground inline-flex items-center gap-2">
                          <BarChart2 size={14} className="text-indigo-600" />
                          Fixed Expenses by Branch
                        </h3>
                        <span className="text-[11px] text-muted-foreground font-mono">
                          {branchSummary.grand_total.items} item(s) tracked
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <div className="bg-card/80 dark:bg-card/40 backdrop-blur rounded-lg p-3 border border-border">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Per Day</div>
                          <div className="font-mono text-lg font-bold text-foreground" data-testid="grand-per-day"><RiyalIcon size="0.9em" /> {branchSummary.grand_total.per_day.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="bg-card/80 dark:bg-card/40 backdrop-blur rounded-lg p-3 border border-border">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Per Month</div>
                          <div className="font-mono text-lg font-bold text-foreground" data-testid="grand-per-month"><RiyalIcon size="0.9em" /> {branchSummary.grand_total.per_month.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="bg-card/80 dark:bg-card/40 backdrop-blur rounded-lg p-3 border border-border">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Per Year</div>
                          <div className="font-mono text-lg font-bold text-foreground" data-testid="grand-per-year"><RiyalIcon size="0.9em" /> {branchSummary.grand_total.per_year.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                            <tr>
                              <th className="text-left py-2 px-2">Branch</th>
                              <th className="text-right py-2 px-2">Items</th>
                              <th className="text-right py-2 px-2">Per Day</th>
                              <th className="text-right py-2 px-2">Per Month</th>
                              <th className="text-right py-2 px-2">Per Year</th>
                              <th className="text-left py-2 px-2 hidden md:table-cell">Frequency mix</th>
                            </tr>
                          </thead>
                          <tbody>
                            {branchSummary.branches.map((b, i) => (
                              <tr key={i} className="border-b border-border/50" data-testid={`branch-row-${b.branch_id || 'unassigned'}`}>
                                <td className="py-2 px-2 font-medium text-foreground">{b.branch_name}</td>
                                <td className="py-2 px-2 text-right font-mono">{b.items}</td>
                                <td className="py-2 px-2 text-right font-mono"><RiyalIcon size="0.8em" /> {b.per_day.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="py-2 px-2 text-right font-mono"><RiyalIcon size="0.8em" /> {b.per_month.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="py-2 px-2 text-right font-mono"><RiyalIcon size="0.8em" /> {b.per_year.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="py-2 px-2 hidden md:table-cell text-[10px] text-muted-foreground">
                                  {Object.entries(b.by_frequency || {}).map(([f, v]) => (
                                    <span key={f} className="inline-block mr-2 px-1.5 py-0.5 rounded bg-muted/60 capitalize">{f}: {v.count}</span>
                                  ))}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-2 italic">
                        Per-month figures normalize quarterly (÷3), yearly (÷12), weekly (×4.345) and daily (×30.4375) recurring entries to a single comparable scale.
                      </p>
                    </div>
                  )}

                  <div id="rec-form" className="hidden mb-4 p-4 bg-muted/50 rounded-xl border space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div><Label className="text-xs">Name *</Label><Input value={newRecData.name} onChange={(e) => setNewRecData({...newRecData, name: e.target.value})} placeholder="Office Rent" className="h-8" /></div>
                      <div><Label className="text-xs">Category</Label><Input value={newRecData.category} onChange={(e) => setNewRecData({...newRecData, category: e.target.value})} placeholder="rent" className="h-8" /></div>
                      <div><Label className="text-xs">Amount *</Label><Input type="number" step="0.01" value={newRecData.amount} onChange={(e) => setNewRecData({...newRecData, amount: e.target.value})} className="h-8" /></div>
                      <div><Label className="text-xs">Frequency</Label><Select value={newRecData.frequency} onValueChange={(v) => setNewRecData({...newRecData, frequency: v})}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="yearly">Yearly</SelectItem></SelectContent></Select></div>
                      <div><Label className="text-xs">Branch</Label><Select value={newRecData.branch_id || "none"} onValueChange={(v) => setNewRecData({...newRecData, branch_id: v === "none" ? "" : v})}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">All</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
                      <div><Label className="text-xs">Next Due *</Label><Input type="date" value={newRecData.next_due_date} onChange={(e) => setNewRecData({...newRecData, next_due_date: e.target.value})} className="h-8" /></div>
                      <div><Label className="text-xs">Alert Days</Label><Input type="number" value={newRecData.alert_days} onChange={(e) => setNewRecData({...newRecData, alert_days: e.target.value})} className="h-8" /></div>
                      <div className="flex items-end"><Button size="sm" className="h-8 rounded-xl w-full" onClick={async () => {
                        if (!newRecData.name || !newRecData.amount || !newRecData.next_due_date) { toast.error('Fill required'); return; }
                        try { await api.post('/recurring-expenses', {...newRecData, amount: parseFloat(newRecData.amount), alert_days: parseInt(newRecData.alert_days) || 7, branch_id: newRecData.branch_id || null, next_due_date: new Date(newRecData.next_due_date).toISOString()}); toast.success('Added'); setNewRecData({name:'',category:'rent',amount:'',frequency:'monthly',branch_id:'',next_due_date:'',alert_days:7}); fetchData(); }
                        catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                      }}>Save</Button></div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {recurringExpenses.map(r => {
                      const dl = r.days_until_due;
                      const overdue = dl != null && dl < 0;
                      const near = dl != null && dl <= (r.alert_days || 7) && dl >= 0;
                      return (
                        <div key={r.id} className={`flex justify-between items-center p-4 rounded-xl border ${overdue ? 'bg-error/5 border-error/30' : near ? 'bg-warning/5 border-warning/30' : 'bg-muted/50'}`}>
                          <div>
                            <div className="font-medium">{r.name}</div>
                            <div className="text-xs text-muted-foreground mt-1 capitalize inline-flex items-center gap-1 flex-wrap">{r.category} | {r.frequency} | <RiyalIcon size="0.85em" /> {r.amount.toFixed(2)}{r.branch_id ? ` | ${branches.find(b => b.id === r.branch_id)?.name || ''}` : ''}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            {dl != null && <Badge className={overdue ? 'bg-error/20 text-error' : near ? 'bg-warning/20 text-warning' : 'bg-success/20 text-success'}>{overdue ? `${Math.abs(dl)}d overdue` : `${dl}d left`}</Badge>}
                            <Button size="sm" variant="default" className="rounded-xl" onClick={() => { setRenewingRec(r); setRenewData({ amount: r.amount.toString(), payment_mode: 'cash', branch_id: r.branch_id || '' }); setShowRenewDialog(true); }}><DollarSign size={14} className="mr-1" />Renew & Pay</Button>
                            <Button size="sm" variant="ghost" className="text-error" onClick={async () => { if(window.confirm('Delete?')) { await api.delete(`/recurring-expenses/${r.id}`); fetchData(); }}}><Trash2 size={12} /></Button>
                          </div>
                        </div>
                      );
                    })}
                    {recurringExpenses.length === 0 && <div className="text-center py-8 text-muted-foreground"><AlertTriangle size={24} className="mx-auto mb-2 text-warning" /><p>No recurring expenses set up yet.</p><p className="text-xs mt-1">Add rent, insurance, subscriptions etc. to get alerts before they're due.</p></div>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Category Manager Dialog */}
        <Dialog open={showCatManager} onOpenChange={setShowCatManager}>
          <DialogContent className="max-w-lg"><DialogHeader><DialogTitle className="font-outfit">Manage Expense Categories</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Add Main Category</Label>
                <div className="flex gap-2 mt-1"><Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="e.g. Marketing" className="h-9" /><Button size="sm" className="rounded-xl h-9" onClick={handleAddCat}>Add</Button></div>
              </div>
              <div>
                <Label className="text-sm font-medium">Add Sub-Category</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={newSubCat.parent || "none"} onValueChange={(v) => setNewSubCat({...newSubCat, parent: v === "none" ? "" : v})}><SelectTrigger className="h-9 w-36"><SelectValue placeholder="Parent" /></SelectTrigger><SelectContent><SelectItem value="none">Select</SelectItem>{mainCats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                  <Input value={newSubCat.name} onChange={(e) => setNewSubCat({...newSubCat, name: e.target.value})} placeholder="Sub-category name" className="h-9 flex-1" />
                  <Button size="sm" className="rounded-xl h-9" onClick={handleAddSubCat}>Add</Button>
                </div>
              </div>
              <div className="border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground mb-2">Current Categories:</p>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">{mainCats.map(cat => {
                  const catRecord = categories.find(c => c.name === cat && !c.parent_id);
                  const catDef = defaultCats.find(d => d.name === cat);
                  const CatIcon = catDef?.icon || MoreHorizontal;
                  const colorClass = catDef?.color || 'bg-muted/50';
                  return (
                  <div key={cat} className={`p-2.5 rounded-lg border ${catRecord ? 'bg-white' : 'bg-muted/50'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm inline-flex items-center gap-1.5"><CatIcon size={14} className="opacity-60" /> {cat} {!catRecord && <span className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">default</span>}</span>
                      {catRecord && <button onClick={() => handleDeleteCat(catRecord.id, cat)} className="text-red-400 hover:text-error transition-colors p-1 rounded hover:bg-error/10" data-testid={`delete-cat-${cat}`} title={`Delete ${cat}`}><Trash2 size={14} /></button>}
                    </div>
                    {getSubCats(cat).length > 0 && <div className="flex gap-1 mt-1.5 flex-wrap">{getSubCats(cat).map(s => {
                      const subRecord = categories.find(c => c.name === s && c.parent_id);
                      return (
                        <Badge key={s} variant="secondary" className="text-xs gap-1">
                          {s}
                          {subRecord && <button onClick={() => handleDeleteCat(subRecord.id, s)} className="text-red-400 hover:text-error ml-0.5" data-testid={`delete-subcat-${s}`} title={`Delete ${s}`}><Trash2 size={10} /></button>}
                        </Badge>
                      );
                    })}</div>}
                  </div>
                );})}</div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Renew & Pay Dialog */}
        <Dialog open={showRenewDialog} onOpenChange={setShowRenewDialog}>
          <DialogContent><DialogHeader><DialogTitle className="font-outfit">Renew & Pay - {renewingRec?.name}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground capitalize">{renewingRec?.category} | {renewingRec?.frequency}</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Amount *</Label><Input type="number" step="0.01" value={renewData.amount} onChange={(e) => setRenewData({...renewData, amount: e.target.value})} /></div>
                <div><Label>Mode</Label><Select value={renewData.payment_mode} onValueChange={(v) => setRenewData({...renewData, payment_mode: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent></Select></div>
                <div><Label>Branch</Label><Select value={renewData.branch_id || "none"} onValueChange={(v) => setRenewData({...renewData, branch_id: v === "none" ? "" : v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">No Branch</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <Button className="rounded-xl" onClick={async () => {
                try { const res = await api.post(`/recurring-expenses/${renewingRec.id}/renew-pay`, {amount: parseFloat(renewData.amount), payment_mode: renewData.payment_mode, branch_id: renewData.branch_id || null}); toast.success(res.data.message); setShowRenewDialog(false); fetchData(); }
                catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }}>Pay & Renew</Button>
            </div>
          </DialogContent>
        </Dialog>
        {/* Expense Refund Dialog */}
        <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
          <DialogContent className="max-w-md" data-testid="refund-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><RotateCcw className="text-error" size={18} /> Record Expense Refund</DialogTitle></DialogHeader>
            <form onSubmit={handleRefundSubmit} className="space-y-4">
              <div className="text-sm text-error bg-error/10 p-2 rounded-lg border border-error/30">
                Record a refund for a returned item or cancelled service. This will create a negative expense entry.
              </div>
              <div className="space-y-2">
                <Label>Refund Amount (SR) *</Label>
                <Input type="number" step="0.01" value={refundData.amount} data-testid="refund-amount"
                  onChange={(e) => setRefundData({ ...refundData, amount: e.target.value })} required placeholder="0.00" className="text-lg font-bold" />
              </div>
              <div className="space-y-2">
                <Label>Reason</Label>
                <Input value={refundData.reason} onChange={(e) => setRefundData({ ...refundData, reason: e.target.value })} placeholder="Returned item, cancelled service, etc." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Refund Mode</Label>
                  <Select value={refundData.refund_mode} onValueChange={(v) => setRefundData({ ...refundData, refund_mode: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="credit">Credit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" value={refundData.date} onChange={(e) => setRefundData({ ...refundData, date: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-error hover:bg-error rounded-xl" data-testid="submit-refund-btn">
                  <RotateCcw size={14} className="mr-1" /> Record Refund
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowRefundDialog(false)} className="rounded-xl">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <WhatsAppSendDialog open={showWhatsApp} onClose={() => setShowWhatsApp(false)} defaultType="expense_summary" branches={branches} />

        {/* iter442e — Bank-statement picker for the Add Expense modal */}
        <PickFromBankDialog
          open={showPickBank}
          onClose={() => setShowPickBank(false)}
          direction="outgoing"
          entityType={formData.supplier_id && !employeeLinkedCats.includes(formData.category) ? 'supplier' : null}
          entityId={formData.supplier_id || null}
          entityName={(suppliers.find(s => s.id === formData.supplier_id) || {}).name || ''}
          defaultSearch={formData.description || ''}
          amountHint={formData.amount || null}
          branches={branches}
          onPick={bulkPickMode ? undefined : (txn) => {
            setPickedBankTxn(txn);
            // Auto-fill amount + date + description from the bank txn
            setFormData(fd => ({
              ...fd,
              amount: String(txn.amount),
              date: txn.date,
              description: fd.description || txn.description,
            }));
            toast.success('Bank line attached — review and Save');
          }}
          onPickMulti={!bulkPickMode ? undefined : async (picks, opts) => {
            // iter448 — Bulk-create N expenses from the selected bank txns.
            // Group refs by paying branch so the existing endpoint
            // (which accepts ONE paying_branch_id per call) handles each
            // branch slice cleanly. Falls back to the form's selected
            // branch when a row has no per-row branch override.
            // iter461 — `opts.dateStrategy` ('per_row' | 'latest') is
            // forwarded to the backend. "latest" collapses a multi-day
            // group (supplier transfer + bank fee + VAT) onto the single
            // latest statement date so the resulting expenses cluster on
            // one settlement day.
            if (!picks?.length) return;
            const dateStrategy = opts?.dateStrategy || 'per_row';
            const isSupplier = !!formData.supplier_id && !employeeLinkedCats.includes(formData.category);
            const payeeType = isSupplier ? 'supplier' : 'expense_category';
            const baseCat = formData.sub_category || formData.category || 'Bank Fees';
            // Bucket by branch (null = "use form default")
            const buckets = new Map();
            for (const p of picks) {
              const bid = p.branch_id || formData.branch_id || '';
              const key = bid || '__default__';
              if (!buckets.has(key)) buckets.set(key, { branch_id: bid || null, refs: [] });
              buckets.get(key).refs.push({ statement_id: p.txn.statement_id, txn_index: p.txn.txn_index });
            }
            let okN = 0;
            let failN = 0;
            for (const { branch_id, refs } of buckets.values()) {
              // The endpoint caps at 50 refs per call — chunk to be safe.
              for (let off = 0; off < refs.length; off += 50) {
                const slice = refs.slice(off, off + 50);
                try {
                  // eslint-disable-next-line no-await-in-loop
                  const r = await api.post('/banking/bank-payments/bulk-from-stmt-txns', {
                    txn_refs: slice,
                    payee_type: payeeType,
                    payee_id: isSupplier ? formData.supplier_id : null,
                    category: !isSupplier ? baseCat : null,
                    sub_category: !isSupplier && formData.sub_category ? formData.sub_category : null,
                    paying_branch_id: branch_id || null,
                    description: formData.description || baseCat,
                    date_strategy: dateStrategy,
                  });
                  okN += (r.data?.created_count || 0);
                  failN += (r.data?.skipped_count || 0);
                } catch (e) {
                  failN += slice.length;
                  toast.error(e?.response?.data?.detail || 'Some bank lines failed');
                }
              }
            }
            if (okN) toast.success(`Created ${okN} expense entr${okN === 1 ? 'y' : 'ies'} from bank statement${failN ? ` (${failN} skipped)` : ''}`);
            else if (failN) toast.error(`All ${failN} lines skipped — they may already be linked`);
            setShowAddDialog(false);
            setShowPickBank(false);
            setPickedBankTxn(null);
            fetchData();
          }}
          onQuickGroup={!bulkPickMode ? undefined : async (g) => {
            // iter449 — one-click bulk book of an entire group as expenses.
            // The decoded label drives the category (falls back to the
            // currently-selected form category, then to "Bank Fees").
            // Auto-journal will fuzzy-match the category against
            // DEFAULT_EXPENSE_ACCOUNT_MAP (e.g. "POS Sale Settlement"
            // routes to Bank Charges & Fees; "VAT - SPAN" / "VAT - MASTER"
            // routes to "VAT on Bank Charges – Input").
            if (!g?.txns?.length) return;
            const category = g.decoded_label || formData.category || 'Bank Fees';
            const refs = g.txns.map(t => ({ statement_id: t.statement_id, txn_index: t.txn_index }));
            const branchId = formData.branch_id || null;
            let okN = 0;
            let failN = 0;
            for (let off = 0; off < refs.length; off += 50) {
              const slice = refs.slice(off, off + 50);
              try {
                // eslint-disable-next-line no-await-in-loop
                const r = await api.post('/banking/bank-payments/bulk-from-stmt-txns', {
                  txn_refs: slice,
                  payee_type: 'expense_category',
                  payee_id: null,
                  category,
                  paying_branch_id: branchId,
                  description: g.key,
                });
                okN += (r.data?.created_count || 0);
                failN += (r.data?.skipped_count || 0);
              } catch (e) {
                failN += slice.length;
                toast.error(e?.response?.data?.detail || 'Quick-categorise failed');
              }
            }
            if (okN) toast.success(`Quick-categorised ${okN} "${g.key}" entr${okN === 1 ? 'y' : 'ies'} → ${category}`);
            else if (failN) toast.error(`All ${failN} lines skipped — already linked?`);
            setShowAddDialog(false);
            setShowPickBank(false);
            setPickedBankTxn(null);
            fetchData();
          }}
        />

        {/* Duplicate Warning Dialog */}
        <AlertDialog open={showDuplicateWarning} onOpenChange={setShowDuplicateWarning}>
          <AlertDialogContent data-testid="expense-duplicate-warning-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
                <AlertTriangle size={20} /> Possible Duplicate Expense
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm">
                <span className="font-bold text-orange-700">{duplicateCount}</span> expense(s) with the same branch and amount
                (<span className="font-bold">{cur()} {parseFloat(formData.amount || 0).toFixed(2)}</span>) already exist on{' '}
                <span className="font-bold">{formData.date}</span>.
                <br /><br />
                Are you sure this is <span className="font-bold">not a duplicate</span> entry?
              </AlertDialogDescription>
              <DuplicatePeekList
                rows={duplicateExisting}
                kind="expense"
                onItemClick={(id) => {
                  setShowDuplicateWarning(false);
                  window.open(`/expenses?focus=${encodeURIComponent(id)}`, '_blank', 'noopener');
                }}
              />
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-exp-duplicate-btn">Cancel & Review</AlertDialogCancel>
              <AlertDialogAction onClick={() => { setShowDuplicateWarning(false); submitExpense(); }} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-exp-duplicate-btn">
                Yes, Save Anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Expense Dialog */}
        <Dialog open={!!editExpense} onOpenChange={(v) => !v && setEditExpense(null)}>
          <DialogContent className="max-w-md" data-testid="edit-expense-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Edit Expense</DialogTitle></DialogHeader>
            {editExpense && (
              <div className="space-y-3">
                <div><Label>Description</Label><Input value={editData.description} onChange={e => setEditData({...editData, description: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Amount</Label><Input type="number" value={editData.amount} onChange={e => setEditData({...editData, amount: parseFloat(e.target.value) || 0})} /></div>
                  <div><Label>Invoice #</Label><Input value={editData.invoice_number} onChange={e => setEditData({...editData, invoice_number: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Payment</Label>
                    <Select value={editData.payment_mode} onValueChange={v => setEditData({...editData, payment_mode: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem><SelectItem value="credit">Credit</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Category</Label><Input value={editData.category} onChange={e => setEditData({...editData, category: e.target.value})} /></div>
                </div>
                <div className="border-t pt-3 mt-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">VAT Settings</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>VAT Rate %</Label><Input type="number" value={editData.vat_rate} onChange={e => setEditData({...editData, vat_rate: parseFloat(e.target.value) || 0})} /></div>
                    <div><Label>VAT Type</Label>
                      <Select value={editData.vat_inclusive ? 'inclusive' : 'exclusive'} onValueChange={v => setEditData({...editData, vat_inclusive: v === 'inclusive'})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="inclusive">Inclusive</SelectItem><SelectItem value="exclusive">Exclusive</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  {editData.vat_rate > 0 && editData.amount > 0 && (
                    <div className="bg-teal-50 rounded-lg p-2.5 text-xs mt-2">
                      <div className="flex justify-between"><span>Net:</span><span className="font-semibold">{editData.vat_inclusive ? (editData.amount / (1 + editData.vat_rate / 100)).toFixed(2) : editData.amount.toFixed(2)}</span></div>
                      <div className="flex justify-between text-teal-700 font-medium"><span>VAT ({editData.vat_rate}%):</span><span>{editData.vat_inclusive ? (editData.amount - editData.amount / (1 + editData.vat_rate / 100)).toFixed(2) : (editData.amount * editData.vat_rate / 100).toFixed(2)}</span></div>
                      <div className="flex justify-between font-bold border-t border-teal-200 pt-1 mt-1"><span>Total:</span><span>{editData.vat_inclusive ? editData.amount.toFixed(2) : (editData.amount * (1 + editData.vat_rate / 100)).toFixed(2)}</span></div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={saveEditExpense} className="rounded-full" data-testid="save-edit-expense-btn">Save</Button>
                  <Button variant="outline" onClick={() => setEditExpense(null)} className="rounded-full">Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Quick-Map dialog: link an unmapped category to a Chart-of-Accounts entry
            without leaving the Expenses page. */}
        <Dialog open={!!quickMapCat} onOpenChange={(open) => { if (!open) setQuickMapCat(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="quick-map-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LinkIcon size={16} className="text-primary" />
                Link "{quickMapCat?.category}" to an account
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Choose the Chart-of-Accounts entry this expense should post to.
              </p>
              <div>
                <Label className="text-xs">Account</Label>
                <select
                  value={quickMapAccount}
                  onChange={(e) => setQuickMapAccount(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="quick-map-account-select"
                >
                  <option value="">— Select an account —</option>
                  {coaAccounts.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.code ? `[${a.code}] ` : ''}{a.name}{a.type ? ` — ${a.type}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Apply to</Label>
                <label className="flex items-start gap-2 p-2.5 rounded-lg border border-border cursor-pointer hover:bg-muted/40" data-testid="quick-map-scope-all">
                  <input type="radio" name="qm-scope" className="mt-0.5" checked={quickMapScope === 'all'} onChange={() => setQuickMapScope('all')} />
                  <span className="text-xs">
                    <span className="font-semibold text-foreground">All future "{quickMapCat?.category}" expenses</span>
                    <span className="block text-muted-foreground">Saves the category mapping — every expense in this category posts here from now on.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 p-2.5 rounded-lg border border-border cursor-pointer hover:bg-muted/40" data-testid="quick-map-scope-once">
                  <input type="radio" name="qm-scope" className="mt-0.5" checked={quickMapScope === 'once'} onChange={() => setQuickMapScope('once')} />
                  <span className="text-xs">
                    <span className="font-semibold text-foreground">Only this expense</span>
                    <span className="block text-muted-foreground">Posts just this one entry here. The category mapping stays unchanged.</span>
                  </span>
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setQuickMapCat(null)} data-testid="quick-map-cancel">Cancel</Button>
              <Button onClick={saveQuickMap} disabled={!quickMapAccount} data-testid="quick-map-save">
                <LinkIcon size={14} className="mr-1.5" />
                {quickMapScope === 'once' ? 'Use for this expense' : 'Save mapping'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter547 — Inline quick-edit dialog for booked auto-entries.
            Lets the user change date / amount / payment mode / notes
            without leaving the Add-Expense dialog flow. */}
        <Dialog open={!!editingExpense} onOpenChange={(v) => !v && setEditingExpense(null)}>
          <DialogContent className="max-w-md" data-testid="auto-booked-edit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil size={16} />
                Edit booked entry
              </DialogTitle>
            </DialogHeader>
            {editingExpense && (
              <div className="space-y-3">
                <div className="text-[11px] text-muted-foreground border border-dashed rounded-md p-2 bg-muted/30 truncate" title={editingExpense.description}>
                  {editingExpense.description || '—'}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-semibold text-muted-foreground">Date</label>
                    <input
                      type="date"
                      value={editingExpense.date || ''}
                      onChange={(e) => setEditingExpense(s => ({ ...s, date: e.target.value }))}
                      className="mt-1 h-9 w-full rounded border border-border px-2 text-sm focus:border-primary focus:outline-none"
                      data-testid="auto-booked-edit-date"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-muted-foreground">Amount (SR)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingExpense.amount}
                      onChange={(e) => setEditingExpense(s => ({ ...s, amount: e.target.value }))}
                      className="mt-1 h-9 w-full rounded border border-border px-2 text-sm font-mono focus:border-primary focus:outline-none"
                      data-testid="auto-booked-edit-amount"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-muted-foreground">Payment mode</label>
                  <select
                    value={editingExpense.payment_mode}
                    onChange={(e) => setEditingExpense(s => ({ ...s, payment_mode: e.target.value }))}
                    className="mt-1 h-9 w-full rounded border border-border bg-white px-2 text-sm focus:border-primary focus:outline-none"
                    data-testid="auto-booked-edit-payment-mode"
                  >
                    <option value="bank">Bank</option>
                    <option value="cash">Cash</option>
                    <option value="transfer">Transfer</option>
                    <option value="card">Card</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-muted-foreground">Notes</label>
                  <input
                    type="text"
                    value={editingExpense.notes}
                    onChange={(e) => setEditingExpense(s => ({ ...s, notes: e.target.value }))}
                    placeholder="Optional"
                    className="mt-1 h-9 w-full rounded border border-border px-2 text-sm focus:border-primary focus:outline-none"
                    data-testid="auto-booked-edit-notes"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditingExpense(null)} data-testid="auto-booked-edit-cancel">
                Cancel
              </Button>
              <Button onClick={saveExpenseEdit} data-testid="auto-booked-edit-save" disabled={!editingExpense?.id}>
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
